import { useState, useEffect, useMemo, useRef, type ChangeEvent } from "react";
import {
  apiService,
  type TutorVersion,
  type TutorVersionCreateRequest,
  type Prompt,
  type PromptVersion,
  type SlotAssignment,
  type ResolvedTutorVersion,
  type NamedLLMConfig,
} from "../services/api.service";
import { APPLICATIONS, DEFAULT_APPLICATION_ID } from "../config/api.config";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, Copy, Check, Trash2, Eye, ArrowUp, ArrowDown, ListChecks, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { OrgCombobox } from "./users-config/OrgCombobox";
import { cn } from "@/lib/utils";
import { readImportFile } from "@/lib/import-bundle";
import type { STTConfig } from "../services/api.service";
import { PromptsCard } from "./tutor-versions/PromptsCard";
import { AssignmentsView } from "./tutor-versions/AssignmentsView";
import { BulkReassignmentModal } from "./tutor-versions/BulkReassignmentModal";
import { NotepadModelsCard } from "./tutor-versions/NotepadModelsCard";
import { mergeNotepadModels, providerFromModel, splitNotepadModels } from "./tutor-versions/notepadModels";
import { GATEWAY_PROMPT_GROUPS, partitionPromptsByApp, promptAppId, gatewayPromptsUpdate } from "./tutor-versions/gatewayPrompts";
interface PromptInput {
  prompt_id: string;
  version: number;
  model: string;
  llm_config_name: string;
  slot_assignments?: SlotAssignment[];
}

interface LLMConfigEntry {
  name: string;
  model: string;
  temperature: number | null;
  max_tokens: number | null;
  // Fallback tiers as bare litellm model strings, in priority order. Provider
  // is derived from the string, same rule as the primary model.
  fallbacks: string[];
  extra: { key: string; value: string }[];
}

type StatusFilter = "all" | "draft" | "published" | "archived" | "dev";
type ViewMode = "cards" | "assignments";

// ── STT provider/model catalog ────────────────────────────────────────────────
// Streaming STT options the ai-tutor backend supports. The tier order defines
// the runtime fallback chain (tier 1 = primary).
const STT_PROVIDER_CATALOG: { value: string; label: string; models: string[] }[] = [
  { value: "cartesia", label: "Cartesia", models: ["ink-2"] },
  { value: "deepgram", label: "Deepgram", models: ["nova-3", "nova-2"] },
  { value: "elevenlabs", label: "ElevenLabs", models: ["scribe_v2_realtime"] },
];

interface SttTier {
  provider: string;
  model: string;
}

// TTS catalog. Cartesia-only for now; values pass straight to the provider API.
// To add a provider/model: append here (and nothing else — purely dashboard-side).
const TTS_PROVIDER_UNSET = "__unset__";
const TTS_MODEL_UNSET = "__unset__";

const TTS_PROVIDERS: { value: string; label: string }[] = [
  { value: "cartesia", label: "Cartesia" },
];

const TTS_MODELS_BY_PROVIDER: Record<string, { value: string; label: string }[]> = {
  cartesia: [
    { value: "sonic-3.6", label: "Sonic 3.6" },
    { value: "sonic-3.5", label: "Sonic 3.5" },
    { value: "sonic-3", label: "Sonic 3" },
  ],
};

// Flat lookup for rendering a model's label in read-only views.
const TTS_MODEL_OPTIONS = Object.values(TTS_MODELS_BY_PROVIDER).flat();

// Hydrate the editor's tier list from a stored stt_config. Tier 1 is the
// primary provider/model; tiers 2+ come from `fallbacks`.
const sttTiersFromConfig = (cfg: STTConfig | null | undefined): SttTier[] => {
  if (!cfg?.provider || !cfg.model) return [];
  const tiers: SttTier[] = [{ provider: cfg.provider, model: cfg.model }];
  for (const f of cfg.fallbacks ?? []) {
    tiers.push({ provider: f.provider, model: f.model });
  }
  return tiers;
};

// Serialize the editor's tier list to stt_config: tier 1 → primary provider/model
// (backward compatible), tiers 2+ → ordered `fallbacks`, each with its own model.
const sttConfigFromTiers = (tiers: SttTier[], language: string): STTConfig | null => {
  const valid = tiers.filter((t) => t.provider && t.model);
  const [first, ...rest] = valid;
  if (!first) return null;
  return {
    provider: first.provider,
    model: first.model,
    ...(language.trim() ? { language: language.trim() } : {}),
    ...(rest.length ? { fallbacks: rest.map((t) => ({ provider: t.provider, model: t.model })) } : {}),
  };
};

const SttTiersEditor = ({
  tiers,
  language,
  onChange,
  onLanguageChange,
}: {
  tiers: SttTier[];
  language: string;
  onChange: (tiers: SttTier[]) => void;
  onLanguageChange: (language: string) => void;
}) => {
  const usedProviders = new Set(tiers.map((t) => t.provider));
  const availableForNew = STT_PROVIDER_CATALOG.filter((p) => !usedProviders.has(p.value));

  const update = (idx: number, patch: Partial<SttTier>) =>
    onChange(tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...tiers];
    const a = next[idx];
    const b = next[idx + dir];
    if (!a || !b) return;
    next[idx] = b;
    next[idx + dir] = a;
    onChange(next);
  };

  const cols = "grid grid-cols-[8.5rem_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-3";

  return (
    <Card className="space-y-3 p-4">
      <h4 className="font-medium">
        STT Config <span className="text-xs font-normal text-muted-foreground">(optional — tier 1 is primary, lower tiers are fallbacks)</span>
      </h4>

      {tiers.length === 0 ? (
        <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">
          No STT tiers configured — the application default will be used.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          {/* Column headers — shown once, not repeated per row */}
          <div className={cn(cols, "border-b bg-muted/40 px-3 py-2")}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tier</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Provider</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Model</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Order</span>
          </div>

          <div className="divide-y">
          {tiers.map((tier, idx) => {
            const catalogEntry = STT_PROVIDER_CATALOG.find((p) => p.value === tier.provider);
            const isPrimary = idx === 0;
            return (
              <div key={idx} className={cn(cols, "px-3 py-2")}>
                {/* Priority indicator — role label only, no serial number */}
                <span className="text-sm font-medium">
                  {isPrimary ? "Primary" : `Fallback ${idx}`}
                </span>

                <Select
                  value={tier.provider}
                  onValueChange={(v) => {
                    const models = STT_PROVIDER_CATALOG.find((p) => p.value === v)?.models || [];
                    update(idx, { provider: v, model: models[0] || "" });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {STT_PROVIDER_CATALOG.filter(
                      (p) => p.value === tier.provider || !usedProviders.has(p.value)
                    ).map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={tier.model}
                  onValueChange={(v) => update(idx, { model: v })}
                  disabled={!tier.provider}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {(catalogEntry?.models || []).map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center">
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    title="Move up (higher priority)"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    disabled={idx === tiers.length - 1}
                    onClick={() => move(idx, 1)}
                    title="Move down (lower priority)"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => onChange(tiers.filter((_, i) => i !== idx))}
                    title="Remove tier"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {availableForNew.length > 0 && (
        <Button
          type="button" variant="outline" size="sm"
          className="w-full border-dashed"
          onClick={() => onChange([...tiers, { provider: "", model: "" }])}
          disabled={tiers.some((t) => !t.provider)}
        >
          <PlusCircle className="mr-1.5 h-4 w-4" /> Add fallback tier
        </Button>
      )}

      <div className="grid gap-2 pt-1 md:w-1/3">
        <Label className="text-xs text-muted-foreground">Language</Label>
        <Input
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          placeholder="e.g. en"
        />
      </div>
    </Card>
  );
};

// ── LLM provider fallback chain ───────────────────────────────────────────────
// Ordered fallback tiers for one LLM config. A tier is the same request with a
// different model string — there is no per-provider client behind it — so this
// editor collects model strings and priority order, nothing else.
const LlmFallbackTiers = ({
  primaryModel,
  fallbacks,
  onChange,
}: {
  primaryModel: string;
  fallbacks: string[];
  onChange: (fallbacks: string[]) => void;
}) => {
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...fallbacks];
    const a = next[idx];
    const b = next[idx + dir];
    if (a === undefined || b === undefined) return;
    next[idx] = b;
    next[idx + dir] = a;
    onChange(next);
  };

  const cols = "grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-center gap-3";

  return (
    <div className="grid gap-2 md:col-span-2">
      <Label className="text-xs text-muted-foreground">
        Fallback chain <span className="font-normal">(optional — tried in order when the tier above is unavailable)</span>
      </Label>

      <div className="overflow-hidden rounded-lg border">
        <div className={cn(cols, "border-b bg-muted/40 px-3 py-2")}>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tier</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Model</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-right">Order</span>
        </div>

        <div className="divide-y">
          {/* Tier 1 mirrors the Model field above — shown so the order reads as one chain */}
          <div className={cn(cols, "px-3 py-2")}>
            <span className="text-sm font-medium">Primary</span>
            <span className="truncate font-mono text-xs text-muted-foreground">
              {primaryModel || "— set the Model field above —"}
            </span>
            <span />
          </div>

          {fallbacks.map((model, idx) => (
            <div key={idx} className={cn(cols, "px-3 py-2")}>
              <span className="text-sm font-medium">Fallback {idx + 1}</span>
              <Input
                value={model}
                onChange={(e) =>
                  onChange(fallbacks.map((m, i) => (i === idx ? e.target.value : m)))
                }
                placeholder="e.g. deepseek/deepseek-chat"
                className="font-mono text-xs"
              />
              <div className="flex items-center">
                <Button
                  type="button" variant="ghost" size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  title="Move up (higher priority)"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button" variant="ghost" size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  disabled={idx === fallbacks.length - 1}
                  onClick={() => move(idx, 1)}
                  title="Move down (lower priority)"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button" variant="ghost" size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => onChange(fallbacks.filter((_, i) => i !== idx))}
                  title="Remove tier"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        type="button" variant="outline" size="sm"
        className="w-full border-dashed"
        onClick={() => onChange([...fallbacks, ""])}
        disabled={fallbacks.some((m) => !m.trim())}
      >
        <PlusCircle className="mr-1.5 h-4 w-4" /> Add fallback tier
      </Button>
    </div>
  );
};

export const TutorVersionList = () => {
  const [tutorVersions, setTutorVersions] = useState<TutorVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTV, setSelectedTV] = useState<TutorVersion | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState<"standard" | "dev">("standard");
  const [isSaving, setIsSaving] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [isOrgDefaultDialogOpen, setIsOrgDefaultDialogOpen] = useState(false);
  const [orgDefaultOrgs, setOrgDefaultOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [orgDefaultLoadingOrgs, setOrgDefaultLoadingOrgs] = useState(false);
  const [tvForOrgDefault, setTvForOrgDefault] = useState<TutorVersion | null>(null);
  const [isRemoveOrgDefaultDialogOpen, setIsRemoveOrgDefaultDialogOpen] = useState(false);
  const [removeOrgDefaultOrgs, setRemoveOrgDefaultOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedRemoveOrgs, setSelectedRemoveOrgs] = useState<string[]>([]);
  const [tvForRemoveOrgDefault, setTvForRemoveOrgDefault] = useState<TutorVersion | null>(null);
  const [allOrgs, setAllOrgs] = useState<{ id: string; name: string }[]>([]);
  const [orgDefaultMap, setOrgDefaultMap] = useState<Record<string, string>>({});
  const [applicationId, setApplicationId] = useState(DEFAULT_APPLICATION_ID);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [orgFilter, setOrgFilter] = useState("all");
  const [view, setView] = useState<ViewMode>("cards");
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [editDraft, setEditDraft] = useState<{ name: string; description: string } | null>(null);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState<{ prompt_id: string; version: number; content: string } | null>(null);
  const [resolvedTV, setResolvedTV] = useState<ResolvedTutorVersion | null>(null);
  const [resolvedPreviewPromptId, setResolvedPreviewPromptId] = useState<string | null>(null);
  const [isResolvingTV, setIsResolvingTV] = useState(false);
  const [resolvedSiblingTV, setResolvedSiblingTV] = useState<ResolvedTutorVersion | null>(null);
  const [isResolvingSiblingTV, setIsResolvingSiblingTV] = useState(false);
  const [resolvedSiblingPreviewPromptId, setResolvedSiblingPreviewPromptId] = useState<string | null>(null);

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [gatewayPrompts, setGatewayPrompts] = useState<Prompt[]>([]);
  // Saving before the gateway list loads would file gateway snapshots under the ai-tutor record.
  const [promptsReady, setPromptsReady] = useState(false);
  const promptsLoadSeq = useRef(0);
  const [siblingLoading, setSiblingLoading] = useState(false);
  const [siblingTV, setSiblingTV] = useState<TutorVersion | null>(null);
  const [promptVersionsMap, setPromptVersionsMap] = useState<
    Record<string, PromptVersion[]>
  >({});
  const [cannedMessagePrompts, setCannedMessagePrompts] = useState<Prompt[]>([]);
  const [gatewayCannedMessagePrompts, setGatewayCannedMessagePrompts] = useState<Prompt[]>([]);
  const allPromptsMapRef = useRef<Map<string, PromptInput>>(new Map());
  // gatewayPrompts includes hidden ones so pinned snapshots map to the gateway record;
  // only visible ones are offered.
  const allAvailablePrompts = useMemo(() => [...prompts, ...gatewayPrompts], [prompts, gatewayPrompts]);
  const offeredGatewayPrompts = useMemo(() => gatewayPrompts.filter((p) => !p.hidden), [gatewayPrompts]);
  const selectedConfigs = useMemo(
    () => splitNotepadModels(selectedTV?.llm_configs, applicationId),
    [selectedTV, applicationId],
  );
  const defaultLLMConfig: LLMConfigEntry = {
    name: "default",
    model: "bedrock/claude-sonnet-4-20250514",
    temperature: null,
    max_tokens: null,
    fallbacks: [],
    extra: [],
  };
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    org: "",
    created_by: "",
    prompts: [] as PromptInput[],
    llm_configs: [{ ...defaultLLMConfig }] as LLMConfigEntry[],
    stt_tiers: [] as SttTier[],
    stt_language: "",
    tts_provider: "",
    tts_model: "",
    canned_messages: [] as { prompt_id: string; version: number }[],
    gateway_canned_messages: [] as { prompt_id: string; version: number }[],
    notepad_models: [] as NamedLLMConfig[],
  });

  const orgOptions = useMemo(() => {
    return [
      { value: "all", label: "All Orgs" },
      { value: "core", label: "No Org" },
      // Use org.id as value — tv.org and orgDefaultMap are both keyed by org id
      ...allOrgs.map((org) => ({ value: org.id, label: org.name })),
    ];
  }, [allOrgs]);

  // id → name lookup for display: tv.org stores org id, UI shows org name
  const orgIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of allOrgs) map[org.id] = org.name;
    return map;
  }, [allOrgs]);

  const filtered = useMemo(() => {
    const sorted = [...tutorVersions].sort((a, b) => b.version_number - a.version_number);
    return sorted.filter((tv) => {
      const matchesStatus = statusFilter === "all" || tv.status === statusFilter;
      const tvOrgId = tv.org?.trim();
      // orgFilter is an org id; tv.org is now also an org id; orgDefaultMap is keyed by org id
      const isOrgDefault = orgFilter !== "all" && orgFilter !== "core" &&
        orgDefaultMap[orgFilter] === tv.tutor_version_id;
      const matchesOrg =
        orgFilter === "all" ||
        isOrgDefault ||
        (orgFilter === "core" ? !tvOrgId : tvOrgId === orgFilter);

      return matchesStatus && matchesOrg;
    });
  }, [tutorVersions, statusFilter, orgFilter, orgDefaultMap]);

  const statusCounts = useMemo(() => {
    const counts = { all: tutorVersions.length, draft: 0, published: 0, archived: 0, dev: 0 };
    tutorVersions.forEach((tv) => {
      const key = tv.status as keyof typeof counts;
      if (key in counts) counts[key] += 1;
    });
    return counts;
  }, [tutorVersions]);

  useEffect(() => {
    loadTutorVersions();
  }, [applicationId]);

  useEffect(() => {
    apiService.getOrganizations().then(setAllOrgs).catch((error) => { setAllOrgs([]); toast.error(error instanceof Error ? error.message : "Failed to load organizations"); });
  }, []);

  useEffect(() => {
    if (showCreateModal || isEditingDraft) {
      loadPrompts();
      loadCannedMessagePrompts();
    }
  }, [showCreateModal, isEditingDraft, applicationId, formData.org]);

  const loadTutorVersions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [tvs, orgDefaults] = await Promise.all([
        apiService.getTutorVersions(applicationId),
        apiService.getOrgDefaultTutorVersionMap(applicationId),
      ]);
      setTutorVersions(tvs);
      setOrgDefaultMap(orgDefaults);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tutor versions");
    } finally {
      setIsLoading(false);
    }
  };

  // A tutor version moves between environments as a file: Export in one
  // dashboard, Import in the other. No server reaches both prompt services.
  const handleExport = async (tv: TutorVersion) => {
    setIsSaving(true);
    try {
      const bundle = await apiService.exportTutorVersionBundle(applicationId, tv.tutor_version_id);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tutor-version-${applicationId}-${tv.tutor_version_id.replace(/[^a-zA-Z0-9-]/g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const aiTutorOnly =
        applicationId === "ai-tutor" &&
        !bundle.applications.some((app) => app.application_id === "gateway");
      toast.success("Export downloaded", {
        description: aiTutorOnly
          ? "Exported ai-tutor only: no gateway version with this id. Import the file in the other environment's dashboard."
          : "Import the file in the other environment's dashboard.",
      });
    } catch (err) {
      toast.error("Export failed", {
        description: err instanceof Error ? err.message : "Failed to export the tutor version",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const read = readImportFile(await file.text());
    if (!read.ok) {
      toast.error("Import failed", { description: read.error });
      return;
    }
    const { bundle } = read;
    const apps = Array.isArray(bundle.applications)
      ? bundle.applications.map((app) => String((app as { application_id?: unknown })?.application_id)).join(", ")
      : "no application";
    if (!confirm(`Import "${String(bundle.name ?? "")}" (${apps}) into this environment?`)) return;
    setIsSaving(true);
    try {
      await apiService.importTutorVersionBundle(bundle);
      toast.success("Tutor version imported");
      await loadTutorVersions();
    } catch (err) {
      toast.error("Import failed", {
        description: err instanceof Error ? err.message : "Failed to import the tutor version",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const loadPrompts = async () => {
    const seq = ++promptsLoadSeq.current;
    setPromptsReady(false);
    try {
      const orgValue = formData.org.trim() || undefined;
      setPrompts(await apiService.getPrompts(applicationId, undefined, orgValue));
      if (applicationId === "ai-tutor") {
        setGatewayPrompts(await apiService.getPrompts("gateway", undefined, orgValue, true));
      } else {
        setGatewayPrompts([]);
      }
      if (seq === promptsLoadSeq.current) setPromptsReady(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load prompts for tutor version creation");
    }
  };

  const loadCannedMessagePrompts = async () => {
    try {
      const data = await apiService.getPrompts(applicationId);
      setCannedMessagePrompts(data.filter((p) => p.tags.includes("canned-message")));
    } catch {
      // Non-critical — canned message selection just won't populate
    }
    if (applicationId === "ai-tutor") {
      try {
        const gwData = await apiService.getPrompts("gateway");
        setGatewayCannedMessagePrompts(gwData.filter((p) => p.tags.includes("canned-message")));
      } catch {
        setGatewayCannedMessagePrompts([]);
      }
    } else {
      setGatewayCannedMessagePrompts([]);
    }
  };

  const getPromptAppId = (promptId: string): string =>
    promptAppId(promptId, allAvailablePrompts, applicationId);

  const findPromptMeta = (promptId: string): Prompt | undefined =>
    allAvailablePrompts.find((p) => p.prompt_id === promptId) ??
    cannedMessagePrompts.find((p) => p.prompt_id === promptId) ??
    gatewayCannedMessagePrompts.find((p) => p.prompt_id === promptId);

  // Prompts whose fetched version list matched the metadata-derived one — no
  // state entry needed, but don't refetch on every dropdown open. Ref, not
  // state: recording a no-op check must not re-render the (large) dialog.
  const versionsVerifiedRef = useRef<Set<string>>(new Set());

  const loadVersionsForPrompt = async (promptId: string, explicitAppId?: string) => {
    if (promptVersionsMap[promptId]?.length || versionsVerifiedRef.current.has(promptId)) return;
    versionsVerifiedRef.current.add(promptId);
    const appId = explicitAppId ?? getPromptAppId(promptId);
    try {
      const versions = await apiService.getPromptVersions(appId, promptId);
      // Skip the state update (and the re-render it causes while a dropdown is
      // open) when the fetched list is exactly 1..latest_version — the dropdown
      // is already showing those options from metadata.
      const nums = versions
        .map((v) => Number.parseInt(v.version, 10))
        .sort((a, b) => a - b);
      const latest = Number.parseInt(findPromptMeta(promptId)?.latest_version ?? "", 10);
      const matchesDerived =
        nums.length > 0 && nums.length === latest && nums.every((n, i) => n === i + 1);
      if (!matchesDerived) {
        setPromptVersionsMap((prev) => ({ ...prev, [promptId]: versions }));
      }
    } catch {
      // Non-critical: dropdown keeps the metadata fallback; allow a retry
      versionsVerifiedRef.current.delete(promptId);
    }
  };

  // Selectable version numbers without hitting the network: prefer the fetched
  // list, else 1..latest_version from prompt metadata (already loaded with the
  // prompt list), else the currently selected version. The fetched list is
  // loaded lazily when a version dropdown is opened.
  const versionOptionsFor = (promptId: string, current: number): number[] => {
    const fetched = promptVersionsMap[promptId];
    if (fetched?.length) return fetched.map((v) => Number.parseInt(v.version, 10));
    const latest = Number.parseInt(findPromptMeta(promptId)?.latest_version ?? "", 10);
    const max = Math.max(1, Number.isNaN(latest) ? 0 : latest, current || 1);
    return Array.from({ length: max }, (_, i) => i + 1);
  };

  const startEditingDraft = async (tv: TutorVersion) => {
    // `fallbacks` is a first-class field below — keep it out of `extra`, which
// stringifies whatever it catches and would flatten the array to "[object Object]".
const extraKeys = ["provider", "model", "temperature", "max_tokens", "fallbacks"];
    const toLLMConfigEntry = (cfg: typeof tv.llm_config, name: string): LLMConfigEntry => {
      return {
        name,
        model: cfg.model,
        temperature: cfg.temperature ?? null,
        max_tokens: (cfg.max_tokens as number) ?? null,
        fallbacks: (cfg.fallbacks ?? []).map((f) => f.model),
        extra: Object.entries(cfg)
          .filter(([k]) => !extraKeys.includes(k))
          .map(([key, value]) => ({ key, value: String(value) })),
      };
    };
    const { generic, notepad } = splitNotepadModels(tv.llm_configs, applicationId);
    const llmConfigs = generic.length
      ? generic.map((nc) => toLLMConfigEntry(nc.config, nc.name))
      : [toLLMConfigEntry(tv.llm_config, "default")];

    // Load sibling gateway TV if editing an ai-tutor version
    let siblingPrompts: PromptInput[] = [];
    if (applicationId === "ai-tutor") {
      try {
        const sibling = await apiService.getTutorVersion("gateway", tv.tutor_version_id);
        setSiblingTV(sibling);
        siblingPrompts = sibling.prompts.map((p) => ({
          prompt_id: p.prompt_id,
          version: p.version,
          model: p.model || "",
          llm_config_name: p.llm_config_name || "",
          slot_assignments: p.slot_assignments ? JSON.parse(JSON.stringify(p.slot_assignments)) : undefined,
        }));
      } catch {
        setSiblingTV(null);
      }
    } else {
      setSiblingTV(null);
    }

    // Fetch canned message prompt IDs so we can separate them from regular prompts
    let cannedIds = new Set<string>();
    let gwCannedIds = new Set<string>();
    try {
      const allPrompts = await apiService.getPrompts(applicationId);
      const canned = allPrompts.filter((p) => p.tags.includes("canned-message"));
      setCannedMessagePrompts(canned);
      cannedIds = new Set(canned.map((p) => p.prompt_id));
    } catch {
      // Fall back to empty — canned messages will appear in the regular prompts section
    }
    if (applicationId === "ai-tutor") {
      try {
        const gwAllPrompts = await apiService.getPrompts("gateway");
        const gwCanned = gwAllPrompts.filter((p) => p.tags.includes("canned-message"));
        setGatewayCannedMessagePrompts(gwCanned);
        gwCannedIds = new Set(gwCanned.map((p) => p.prompt_id));
      } catch {
        setGatewayCannedMessagePrompts([]);
      }
    }

    // Collect all prompt_ids that are slot option references — these exist as top-level
    // entries in tv.prompts[] but should not appear as editable top-level prompts in the
    // UI (they're referenced via slot_assignments on parent prompts).
    const slotOptionIds = new Set<string>();
    for (const p of tv.prompts) {
      for (const slot of p.slot_assignments ?? []) {
        for (const opt of slot.options) {
          if (opt.prompt_id) slotOptionIds.add(opt.prompt_id);
        }
      }
    }

    const regularPrompts = tv.prompts.filter((p) => !cannedIds.has(p.prompt_id) && !slotOptionIds.has(p.prompt_id));
    const cannedPrompts = tv.prompts.filter((p) => cannedIds.has(p.prompt_id));
    // Keep ALL sibling prompts (including sub-prompts) in formData so saves stay flat-complete.
    // Sub-prompts are hidden in the display layer of the Gateway Prompts edit card.
    const regularSiblingPrompts = siblingPrompts.filter((p) => !gwCannedIds.has(p.prompt_id));
    const gwCannedPrompts = siblingPrompts.filter((p) => gwCannedIds.has(p.prompt_id));

    // Build lookup map for recursive collectSlotOptionPrompts
    const editAllMap = new Map<string, PromptInput>();
    for (const p of tv.prompts) {
      editAllMap.set(p.prompt_id, {
        prompt_id: p.prompt_id,
        version: p.version,
        model: p.model || "",
        llm_config_name: p.llm_config_name || "",
        slot_assignments: p.slot_assignments ? JSON.parse(JSON.stringify(p.slot_assignments)) : undefined,
      });
    }
    for (const p of siblingPrompts) editAllMap.set(p.prompt_id, p);
    allPromptsMapRef.current = editAllMap;

    setFormData({
      name: tv.name,
      description: tv.description || "",
      org: tv.org || "",
      created_by: tv.created_by || "",
      prompts: [
        ...regularPrompts.map((p) => ({
          prompt_id: p.prompt_id,
          version: p.version,
          model: p.model || "",
          llm_config_name: p.llm_config_name || "",
          slot_assignments: p.slot_assignments ? JSON.parse(JSON.stringify(p.slot_assignments)) : undefined,
        })),
        ...regularSiblingPrompts,
      ],
      llm_configs: llmConfigs,
      stt_tiers: sttTiersFromConfig(tv.stt_config),
      stt_language: tv.stt_config?.language || "",
      tts_provider: tv.tts_config?.provider || "",
      tts_model: tv.tts_config?.model || "",
      canned_messages: cannedPrompts.map((p) => ({
        prompt_id: p.prompt_id,
        version: p.version,
      })),
      gateway_canned_messages: gwCannedPrompts.map((p) => ({
        prompt_id: p.prompt_id,
        version: p.version,
      })),
      notepad_models: notepad,
    });

    // Prompt versions load lazily when a version dropdown is opened — eagerly
    // fetching all of them here bursts the AppConfig rate limit (throttled
    // requests came back 500 and dropdowns silently fell back to v1).

    setIsEditingDraft(true);
  };

  const handleSaveDraft = async () => {
    if (!selectedTV) return;
    setIsSaving(true);
    try {
      const primaryConfig = buildLLMConfig(formData.llm_configs[0]);
      const namedConfigs = mergeNotepadModels(
        formData.llm_configs.map((c) => ({ name: c.name, config: buildLLMConfig(c) })),
        formData.notepad_models,
        applicationId,
      );

      // Merge canned messages into prompts array
      const topLevelUpdatePrompts = [
        ...formData.prompts.map((p) => ({
          prompt_id: p.prompt_id,
          version: p.version,
          model: p.model || undefined,
          llm_config_name: p.llm_config_name || undefined,
          slot_assignments: p.slot_assignments && p.slot_assignments.length > 0 ? p.slot_assignments : undefined,
        })),
        ...formData.canned_messages.filter((cm) => cm.prompt_id).map((cm) => ({
          prompt_id: cm.prompt_id,
          version: cm.version,
        })),
        ...formData.gateway_canned_messages.filter((cm) => cm.prompt_id).map((cm) => ({
          prompt_id: cm.prompt_id,
          version: cm.version,
        })),
      ];
      // Append slot option prompts as flat top-level entries (backend flat structure)
      const updateTopLevelIds = new Set(topLevelUpdatePrompts.map((p) => p.prompt_id));
      const allUpdatePrompts = [...topLevelUpdatePrompts, ...collectSlotOptionPrompts(formData.prompts, updateTopLevelIds, allPromptsMapRef.current)];

      const { primary: primaryPrompts, gateway: gwPrompts } =
        partitionPromptsByApp(allUpdatePrompts, allAvailablePrompts, applicationId);

      await apiService.updateTutorVersion(applicationId, selectedTV.tutor_version_id, {
        name: formData.name,
        description: formData.description,
        prompts: primaryPrompts,
        llm_config: primaryConfig,
        llm_configs: namedConfigs,
        stt_config: sttConfigFromTiers(formData.stt_tiers, formData.stt_language),
        tts_config: formData.tts_provider.trim() && formData.tts_model.trim()
          ? {
              provider: formData.tts_provider.trim(),
              model: formData.tts_model.trim(),
            }
          : null,
      });

      if (siblingTV) {
        const gwPromptsUpdate = gatewayPromptsUpdate(siblingTV.prompts.length, gwPrompts);
        await apiService.updateTutorVersion("gateway", selectedTV.tutor_version_id, {
          name: formData.name,
          description: formData.description,
          ...gwPromptsUpdate,
          llm_config: primaryConfig,
          llm_configs: namedConfigs,
        });
        if (!("prompts" in gwPromptsUpdate)) {
          toast.warning("Gateway prompts kept", {
            description: "Saving would have removed every gateway prompt, so the gateway record keeps its prompts.",
          });
        }
      }

      const updated = await apiService.getTutorVersion(applicationId, selectedTV.tutor_version_id);
      setSelectedTV(updated);
      setIsEditingDraft(false);
      await loadTutorVersions();
    } catch (err: any) {
      setError(err?.message || "Failed to save draft");
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handlePublish = async (tv: TutorVersion) => {
    setIsSaving(true);
    try {
      await apiService.publishTutorVersion(applicationId, tv.tutor_version_id);
      await loadTutorVersions();
      setSelectedTV(await apiService.getTutorVersion(applicationId, tv.tutor_version_id));
    } catch (err: any) {
      setError(err?.message || "Failed to publish");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (tv: TutorVersion) => {
    setIsSaving(true);
    try {
      await apiService.archiveTutorVersion(applicationId, tv.tutor_version_id);
      await loadTutorVersions();
      setSelectedTV(await apiService.getTutorVersion(applicationId, tv.tutor_version_id));
    } catch (err: any) {
      setError(err?.message || "Failed to archive");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (tv: TutorVersion) => {
    setIsSaving(true);
    try {
      await apiService.setDefaultTutorVersion(applicationId, tv.tutor_version_id);
      await loadTutorVersions();
      setSelectedTV(await apiService.getTutorVersion(applicationId, tv.tutor_version_id));
    } catch (err: any) {
      setError(err?.message || "Failed to set default");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetOrgDefault = async (tv: TutorVersion) => {
    setTvForOrgDefault(tv);
    setIsOrgDefaultDialogOpen(true);
    if (allOrgs.length > 0) {
      setOrgDefaultOrgs(allOrgs);
    } else {
      setOrgDefaultLoadingOrgs(true);
      try {
        const orgs = await apiService.getOrganizations();
        setOrgDefaultOrgs(orgs);
        setAllOrgs(orgs);
      } catch {
        setOrgDefaultOrgs([]);
      } finally {
        setOrgDefaultLoadingOrgs(false);
      }
    }
  };

  const handleConfirmOrgDefault = async () => {
    if (!tvForOrgDefault || selectedOrgs.length === 0) return;
    setIsSaving(true);
    try {
      await Promise.all(
        selectedOrgs.map((orgId) =>
          apiService.setOrgDefaultTutorVersion(applicationId, orgId, tvForOrgDefault.tutor_version_id),
        ),
      );
      setIsOrgDefaultDialogOpen(false);
      setSelectedOrgs([]);
      setTvForOrgDefault(null);
      apiService.getOrgDefaultTutorVersionMap(applicationId).then(setOrgDefaultMap).catch((error) => toast.error(error instanceof Error ? error.message : "Failed to load organization defaults"));
    } catch (err: any) {
      setError(err?.message || "Failed to set org default");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveOrgDefault = (tv: TutorVersion) => {
    setTvForRemoveOrgDefault(tv);
    // Find orgs where this TV is currently set as default
    const orgsWithDefault = Object.entries(orgDefaultMap)
      .filter(([, tvId]) => tvId === tv.tutor_version_id)
      .map(([orgId]) => ({ id: orgId, name: orgIdToName[orgId] || orgId }));
    setRemoveOrgDefaultOrgs(orgsWithDefault);
    setSelectedRemoveOrgs([]);
    setIsRemoveOrgDefaultDialogOpen(true);
  };

  const handleConfirmRemoveOrgDefault = async () => {
    if (!tvForRemoveOrgDefault || selectedRemoveOrgs.length === 0) return;
    setIsSaving(true);
    try {
      await Promise.all(
        selectedRemoveOrgs.map((orgId) =>
          apiService.removeOrgDefaultTutorVersion(applicationId, orgId),
        ),
      );
      setIsRemoveOrgDefaultDialogOpen(false);
      setSelectedRemoveOrgs([]);
      setTvForRemoveOrgDefault(null);
      apiService.getOrgDefaultTutorVersionMap(applicationId).then(setOrgDefaultMap).catch((error) => toast.error(error instanceof Error ? error.message : "Failed to load organization defaults"));
    } catch (err: any) {
      setError(err?.message || "Failed to remove org default");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (tv: TutorVersion) => {
    if (!confirm(`Delete tutor version "${tv.name}"?`)) return;
    setIsSaving(true);
    try {
      await apiService.deleteTutorVersion(applicationId, tv.tutor_version_id);
      setSelectedTV(null);
      await loadTutorVersions();
    } catch (err: any) {
      setError(err?.message || "Failed to delete");
    } finally {
      setIsSaving(false);
    }
  };

  // Collect all slot option prompts from a prompts list as flat top-level entries (deduped).
  // The backend stores the flat structure: every prompt referenced in any slot option
  // must also appear as a top-level entry in the prompts[] array.
  // Recurses through nested slot_assignments so multi-level nesting is captured.
  const collectSlotOptionPrompts = (
    promptList: PromptInput[],
    existingIds: Set<string>,
    allPromptsById?: Map<string, PromptInput>,
  ): { prompt_id: string; version: number; slot_assignments?: SlotAssignment[] }[] => {
    const entries: { prompt_id: string; version: number; slot_assignments?: SlotAssignment[] }[] = [];
    for (const p of promptList) {
      for (const slot of p.slot_assignments ?? []) {
        for (const opt of slot.options) {
          if (opt.prompt_id && opt.version != null && !existingIds.has(opt.prompt_id)) {
            existingIds.add(opt.prompt_id);
            const full = allPromptsById?.get(opt.prompt_id);
            const sa = full?.slot_assignments;
            const entry = { prompt_id: opt.prompt_id, version: opt.version, ...(sa ? { slot_assignments: sa } : {}) };
            entries.push(entry);
            if (sa && sa.length > 0) {
              entries.push(...collectSlotOptionPrompts([entry as PromptInput], existingIds, allPromptsById));
            }
          }
        }
      }
    }
    return entries;
  };

  const buildLLMConfig = (entry: LLMConfigEntry) => {
    const extraKwargs: Record<string, unknown> = {};
    for (const { key, value } of entry.extra) {
      if (!key.trim()) continue;
      const trimmed = value.trim();
      if (trimmed === "true") extraKwargs[key.trim()] = true;
      else if (trimmed === "false") extraKwargs[key.trim()] = false;
      else if (trimmed !== "" && !Number.isNaN(Number(trimmed))) {
        extraKwargs[key.trim()] = Number(trimmed);
      } else {
        extraKwargs[key.trim()] = trimmed;
      }
    }
    // Derive provider from litellm model string (e.g. "bedrock/claude-sonnet-4-20250514" -> "bedrock")
    const cfg: TutorVersionCreateRequest["llm_config"] = {
      provider: providerFromModel(entry.model),
      model: entry.model,
      ...extraKwargs,
    };
    if (entry.temperature != null) cfg.temperature = entry.temperature;
    if (entry.max_tokens != null) cfg.max_tokens = entry.max_tokens;
    // Ordered fallback tiers. Blank rows are dropped so a half-typed tier never
    // reaches the runtime chain.
    const fallbacks = entry.fallbacks
      .map((m) => m.trim())
      .filter(Boolean)
      .map((model) => ({ provider: providerFromModel(model), model }));
    if (fallbacks.length) cfg.fallbacks = fallbacks;
    return cfg;
  };

  const handleCreate = async () => {
    setIsSaving(true);
    try {
      const primaryConfig = buildLLMConfig(formData.llm_configs[0]);
      const namedConfigs = mergeNotepadModels(
        formData.llm_configs.map((c) => ({ name: c.name, config: buildLLMConfig(c) })),
        formData.notepad_models,
        applicationId,
      );

      // Normalize slot_assignments: ensure exactly one default per slot (first option if none selected)
      const normalizeSlots = (slots?: SlotAssignment[]): SlotAssignment[] | undefined => {
        if (!slots || slots.length === 0) return undefined;
        return slots.map((slot) => {
          if (slot.options.length === 0) return slot;
          const hasDefault = slot.options.some((o) => o.default);
          if (hasDefault) return slot;
          return { ...slot, options: slot.options.map((o, i) => ({ ...o, default: i === 0 })) };
        });
      };

      // Merge canned messages into prompts array (they're just prompts tagged "canned-message")
      const topLevelPrompts = [
        ...formData.prompts.map((p) => ({
          prompt_id: p.prompt_id,
          version: p.version,
          model: p.model || undefined,
          llm_config_name: p.llm_config_name || undefined,
          ...((() => { const ns = normalizeSlots(p.slot_assignments); return ns ? { slot_assignments: ns } : {}; })()),
        })),
        ...formData.canned_messages.filter((cm) => cm.prompt_id).map((cm) => ({
          prompt_id: cm.prompt_id,
          version: cm.version,
        })),
        ...formData.gateway_canned_messages.filter((cm) => cm.prompt_id).map((cm) => ({
          prompt_id: cm.prompt_id,
          version: cm.version,
        })),
      ];
      // Append slot option prompts as flat top-level entries (backend flat structure)
      const topLevelIds = new Set(topLevelPrompts.map((p) => p.prompt_id));
      const allPrompts = [...topLevelPrompts, ...collectSlotOptionPrompts(formData.prompts, topLevelIds, allPromptsMapRef.current)];

      const { primary: aiTutorPrompts, gateway: gwCreatePrompts } =
        partitionPromptsByApp(allPrompts, allAvailablePrompts, applicationId);

      const request: TutorVersionCreateRequest = {
        application_id: applicationId,
        name: formData.name,
        description: formData.description,
        org: formData.org.trim() || undefined,
        prompts: aiTutorPrompts,
        llm_config: primaryConfig,
        llm_configs: namedConfigs,
      };

      // The gateway reads its prompts and notepad models only from its own record.
      if (gwCreatePrompts.length > 0 || formData.notepad_models.length > 0) {
        request.applications = [
          {
            application_id: "ai-tutor",
            prompts: aiTutorPrompts,
            llm_config: primaryConfig,
            llm_configs: namedConfigs,
          },
          {
            application_id: "gateway",
            prompts: gwCreatePrompts,
            llm_config: primaryConfig,
            llm_configs: namedConfigs,
          },
        ];
      }
      const createSttConfig = sttConfigFromTiers(formData.stt_tiers, formData.stt_language);
      if (createSttConfig) {
        request.stt_config = createSttConfig;
      }
      if (formData.tts_provider.trim() && formData.tts_model.trim()) {
        request.tts_config = {
          provider: formData.tts_provider.trim(),
          model: formData.tts_model.trim(),
        };
      }
      if (createMode === "dev") {
        await apiService.createDevTutorVersion({
          ...request,
          created_by: formData.created_by.trim() || "dev",
        });
      } else {
        await apiService.createTutorVersion(request);
      }
      setShowCreateModal(false);
      setCreateMode("standard");
      setFormData({
        name: "",
        description: "",
        org: "",
        created_by: "",
        prompts: [],
        llm_configs: [{ ...defaultLLMConfig }],
        stt_tiers: [],
        stt_language: "",
        tts_provider: "",
        tts_model: "",
        canned_messages: [],
        gateway_canned_messages: [],
        notepad_models: [],
      });
      setPromptVersionsMap({});
      await loadTutorVersions();
    } catch (err: any) {
      setError(err?.message || "Failed to create tutor version");
    } finally {
      setIsSaving(false);
    }
  };

  const copyRuntimeEndpoint = (tv: TutorVersion) => {
    const url = `/api/v1/runtime/tutor-version/${tv.application_id}/${tv.tutor_version_id}`;
    navigator.clipboard.writeText(url);
    setCopiedEndpoint(true);
    setTimeout(() => setCopiedEndpoint(false), 1800);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={applicationId} onValueChange={setApplicationId}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {APPLICATIONS.map((app) => (
                <SelectItem key={app.value} value={app.value}>{app.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tabs
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as StatusFilter)}
          >
            <TabsList>
              {(["all", "draft", "published", "archived", "dev"] as StatusFilter[]).map((s) => (
                <TabsTrigger key={s} value={s} className="capitalize">
                  {s} <span className="ml-1 text-xs opacity-70">{statusCounts[s]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by org" />
            </SelectTrigger>
            <SelectContent>
              {orgOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          {applicationId === DEFAULT_APPLICATION_ID && <BulkReassignmentModal versions={tutorVersions} organizations={allOrgs} onApplied={loadTutorVersions} />}
          {/* The assignments feature is hard-wired to ai-tutor (+ gateway): the
              status data, the defaults, and the unpin action all act on the
              ai-tutor world regardless of the selected application. */}
          {applicationId === DEFAULT_APPLICATION_ID && (
            <Tabs value={view} onValueChange={(v) => setView(v as ViewMode)}>
              <TabsList>
                <TabsTrigger value="cards">Cards</TabsTrigger>
                <TabsTrigger value="assignments">
                  <ListChecks className="mr-1 size-3.5" />
                  Assignments
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            variant="outline"
            onClick={() => importInputRef.current?.click()}
            disabled={isSaving}
            title="Import a tutor version exported from another environment"
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setCreateMode("dev");
              setShowCreateModal(true);
            }}
          >
            <PlusCircle className="h-4 w-4" />
            New Dev Version
          </Button>
          <Button onClick={() => { setCreateMode("standard"); setShowCreateModal(true); }}>
            <PlusCircle className="h-4 w-4" />
            New Tutor Version
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card className="p-8 text-center text-muted-foreground">Loading tutor versions...</Card>
      ) : error ? (
        <Card className="space-y-3 p-8 text-center">
          <p className="text-destructive">{error}</p>
          <Button onClick={loadTutorVersions}>Retry</Button>
        </Card>
      ) : view === "assignments" && applicationId === DEFAULT_APPLICATION_ID ? (
        <AssignmentsView versions={tutorVersions} />
      ) : filtered.length === 0 ? (
        <Card className="space-y-3 p-8 text-center">
          <h3 className="font-semibold">No tutor versions found</h3>
          <p className="text-sm text-muted-foreground">
            Create immutable tutor snapshots to pin prompt and model settings.
          </p>
          <Button onClick={() => setShowCreateModal(true)}>Create Tutor Version</Button>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((tv) => (
            <Card
              key={tv.tutor_version_id}
              className="cursor-pointer space-y-3 p-4 transition hover:border-primary/40"
              onClick={async () => {
                setSelectedTV(tv);
                // Load sibling gateway TV for read-only view
                if (applicationId === "ai-tutor") {
                  setSiblingLoading(true);
                  try {
                    const sibling = await apiService.getTutorVersion("gateway", tv.tutor_version_id);
                    setSiblingTV(sibling);
                  } catch {
                    setSiblingTV(null);
                  } finally {
                    setSiblingLoading(false);
                  }
                } else {
                  setSiblingTV(null);
                }
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant={tv.status === "published" ? "default" : tv.status === "dev" ? "outline" : "secondary"}>{tv.status}</Badge>
                  <Badge variant="outline">v{tv.version_number}</Badge>
                  {tv.is_default && <Badge variant="default">default</Badge>}
                  {tv.org && <Badge variant="secondary">{orgIdToName[tv.org] || tv.org}</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">{tv.prompts.length} prompts</span>
              </div>
              <div>
                <h3 className="font-semibold">{tv.name}</h3>
                <p className="font-mono text-xs text-muted-foreground">{tv.tutor_version_id}</p>
                {tv.description && (
                  <p className="line-clamp-2 text-sm text-muted-foreground">{tv.description}</p>
                )}
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p className="truncate">Model: {tv.llm_config.model}</p>
                <p>Created {formatDate(tv.created_at)}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) setCreateMode("standard"); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{createMode === "dev" ? "Create Dev Tutor Version" : "Create Tutor Version"}</DialogTitle>
            <DialogDescription>
              {createMode === "dev"
                ? "Create a mutable dev version for testing. Uses username as TV ID prefix."
                : "Snapshot prompts and LLM configuration into an immutable version."}
            </DialogDescription>
          </DialogHeader>

          {tutorVersions.length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-dashed p-3">
              <Label className="shrink-0 text-muted-foreground">Duplicate from:</Label>
              <Select
                value="none"
                onValueChange={async (tvId) => {
                  if (tvId === "none") return;
                  try {
                    const src = await apiService.getTutorVersion(applicationId, tvId);
                    // `fallbacks` is a first-class field below — keep it out of `extra`, which
// stringifies whatever it catches and would flatten the array to "[object Object]".
const extraKeys = ["provider", "model", "temperature", "max_tokens", "fallbacks"];
                    const toLLMConfigEntry = (cfg: typeof src.llm_config, name: string): LLMConfigEntry => {
                      return {
                        name,
                        model: cfg.model,
                        temperature: cfg.temperature ?? null,
                        max_tokens: (cfg.max_tokens as number) ?? null,
                        fallbacks: (cfg.fallbacks ?? []).map((f) => f.model),
                        extra: Object.entries(cfg)
                          .filter(([k]) => !extraKeys.includes(k))
                          .map(([key, value]) => ({ key, value: String(value) })),
                      };
                    };
                    const { generic, notepad } = splitNotepadModels(src.llm_configs, applicationId);
                    const llmConfigs = generic.length
                      ? generic.map((nc) => toLLMConfigEntry(nc.config, nc.name))
                      : [toLLMConfigEntry(src.llm_config, "default")];
                    // Also load sibling gateway prompts for duplication
                    let siblingDupPrompts: PromptInput[] = [];
                    if (applicationId === "ai-tutor") {
                      try {
                        const sibling = await apiService.getTutorVersion("gateway", src.tutor_version_id);
                        siblingDupPrompts = sibling.prompts.map((p) => ({
                          prompt_id: p.prompt_id,
                          version: p.version,
                          model: p.model || "",
                          llm_config_name: p.llm_config_name || "",
                          slot_assignments: p.slot_assignments ? JSON.parse(JSON.stringify(p.slot_assignments)) : undefined,
                        }));
                      } catch {
                        // No sibling record
                      }
                    }
                    // Fetch canned message prompt IDs to separate them from regular prompts
                    let dupCannedIds = new Set<string>();
                    let dupGwCannedIds = new Set<string>();
                    try {
                      const allPrompts = await apiService.getPrompts(applicationId);
                      const canned = allPrompts.filter((p) => p.tags.includes("canned-message"));
                      setCannedMessagePrompts(canned);
                      dupCannedIds = new Set(canned.map((p) => p.prompt_id));
                    } catch { /* ignore */ }
                    if (applicationId === "ai-tutor") {
                      try {
                        const gwAllPrompts = await apiService.getPrompts("gateway");
                        const gwCanned = gwAllPrompts.filter((p) => p.tags.includes("canned-message"));
                        setGatewayCannedMessagePrompts(gwCanned);
                        dupGwCannedIds = new Set(gwCanned.map((p) => p.prompt_id));
                      } catch {
                        setGatewayCannedMessagePrompts([]);
                      }
                    }

                    // Filter out nested slot option prompts — they are stored flat but
                    // referenced via slot_assignments on parent prompts
                    const srcSlotOptionIds = new Set<string>();
                    for (const p of src.prompts) {
                      for (const slot of p.slot_assignments ?? []) {
                        for (const opt of slot.options) {
                          if (opt.prompt_id) srcSlotOptionIds.add(opt.prompt_id);
                        }
                      }
                    }
                    const toPromptInput = (p: typeof src.prompts[0]): PromptInput => ({
                      prompt_id: p.prompt_id,
                      version: p.version,
                      model: p.model || "",
                      llm_config_name: p.llm_config_name || "",
                      slot_assignments: p.slot_assignments ? JSON.parse(JSON.stringify(p.slot_assignments)) : undefined,
                    });

                    // Build lookup map for all prompts (including sub-prompts) so recursive
                    // collectSlotOptionPrompts can find nested slot_assignments at save time
                    const dupAllMap = new Map<string, PromptInput>();
                    for (const p of src.prompts) dupAllMap.set(p.prompt_id, toPromptInput(p));
                    for (const p of siblingDupPrompts) dupAllMap.set(p.prompt_id, p);
                    allPromptsMapRef.current = dupAllMap;

                    const regularSrcPrompts = src.prompts.filter(
                      (p) => !dupCannedIds.has(p.prompt_id) && !srcSlotOptionIds.has(p.prompt_id),
                    );
                    const cannedSrcPrompts = src.prompts.filter((p) => dupCannedIds.has(p.prompt_id));
                    const regularSibPrompts = siblingDupPrompts.filter(
                      (p) => !dupGwCannedIds.has(p.prompt_id),
                    );
                    const cannedSibPrompts = siblingDupPrompts.filter((p) => dupGwCannedIds.has(p.prompt_id));

                    setFormData({
                      name: `${src.name} (copy)`,
                      description: src.description || "",
                      org: src.org || "",
                      created_by: src.created_by || "",
                      prompts: [
                        ...regularSrcPrompts.map(toPromptInput),
                        ...regularSibPrompts,
                      ],
                      llm_configs: llmConfigs,
                      stt_tiers: sttTiersFromConfig(src.stt_config),
                      stt_language: src.stt_config?.language || "",
                      tts_provider: src.tts_config?.provider || "",
                      tts_model: src.tts_config?.model || "",
                      canned_messages: cannedSrcPrompts.map((p) => ({
                        prompt_id: p.prompt_id,
                        version: p.version,
                      })),
                      gateway_canned_messages: cannedSibPrompts.map((p) => ({
                        prompt_id: p.prompt_id,
                        version: p.version,
                      })),
                      notepad_models: notepad,
                    });
                    // Prompt versions load lazily when a version dropdown is
                    // opened — see comment in startEditingDraft.
                  } catch {
                    setError("Failed to load tutor version for duplication");
                  }
                }}
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Select a version to copy..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a version to copy...</SelectItem>
                  {tutorVersions.map((tv) => (
                    <SelectItem key={tv.tutor_version_id} value={tv.tutor_version_id}>
                      v{tv.version_number} — {tv.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="Mid-term prep config"
              />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                className="min-h-20 rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="grid gap-2">
              <Label>Organization</Label>
              <OrgCombobox
                value={formData.org || undefined}
                onChange={(id) => setFormData((p) => ({ ...p, org: id ?? "" }))}
              />
            </div>

            {createMode === "dev" && (
              <div className="grid gap-2">
                <Label>Created By (username for TV ID)</Label>
                <Input
                  value={formData.created_by}
                  onChange={(e) => setFormData((p) => ({ ...p, created_by: e.target.value }))}
                  placeholder="e.g. rithwik"
                />
              </div>
            )}

            <Card className="space-y-3 p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">LLM Configs</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormData((p) => ({
                      ...p,
                      llm_configs: [
                        ...p.llm_configs,
                        { ...defaultLLMConfig, name: `config-${p.llm_configs.length + 1}`, model: "" },
                      ],
                    }))
                  }
                >
                  <PlusCircle className="h-3 w-3" />
                  Add Config
                </Button>
              </div>

              {formData.llm_configs.map((cfg, ci) => {
                const updateCfg = (patch: Partial<LLMConfigEntry>) =>
                  setFormData((p) => ({
                    ...p,
                    llm_configs: p.llm_configs.map((c, idx) =>
                      idx === ci ? { ...c, ...patch } : c,
                    ),
                  }));
                return (
                  <div key={ci} className="space-y-3 rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={cfg.name}
                        onChange={(e) => updateCfg({ name: e.target.value })}
                        className="max-w-[200px] font-medium"
                        placeholder="Config name"
                      />
                      {ci === 0 && (
                        <Badge variant="secondary" className="text-xs">primary</Badge>
                      )}
                      {ci > 0 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-auto"
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              llm_configs: p.llm_configs.filter((_, idx) => idx !== ci),
                            }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="grid gap-2 md:col-span-2">
                        <Label>Model</Label>
                        <Input
                          value={cfg.model}
                          onChange={(e) => updateCfg({ model: e.target.value })}
                          placeholder="e.g. bedrock/claude-sonnet-4-20250514, azure/gpt-5"
                        />
                        <p className="text-xs text-muted-foreground">
                          Use litellm format: provider/model (e.g. bedrock/claude-sonnet-4-20250514, azure/gpt-5)
                        </p>
                      </div>
                      <LlmFallbackTiers
                        primaryModel={cfg.model}
                        fallbacks={cfg.fallbacks}
                        onChange={(fallbacks) => updateCfg({ fallbacks })}
                      />
                      <div className="grid gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`temp-toggle-${ci}`}
                            checked={cfg.temperature != null}
                            onChange={(e) =>
                              updateCfg({ temperature: e.target.checked ? 0.7 : null })
                            }
                            className="h-3.5 w-3.5"
                          />
                          <Label htmlFor={`temp-toggle-${ci}`}>Temperature</Label>
                        </div>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="2"
                          disabled={cfg.temperature == null}
                          value={cfg.temperature ?? ""}
                          onChange={(e) =>
                            updateCfg({
                              temperature: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          placeholder="Not set"
                        />
                      </div>
                      <div className="grid gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`tokens-toggle-${ci}`}
                            checked={cfg.max_tokens != null}
                            onChange={(e) =>
                              updateCfg({ max_tokens: e.target.checked ? 4096 : null })
                            }
                            className="h-3.5 w-3.5"
                          />
                          <Label htmlFor={`tokens-toggle-${ci}`}>Max tokens</Label>
                        </div>
                        <Input
                          type="number"
                          disabled={cfg.max_tokens == null}
                          value={cfg.max_tokens ?? ""}
                          onChange={(e) =>
                            updateCfg({
                              max_tokens: e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          placeholder="Not set"
                        />
                      </div>
                    </div>
                    {/* Additional params */}
                    <div className="border-t pt-2">
                      <div className="mb-2 flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Additional Parameters</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            updateCfg({ extra: [...cfg.extra, { key: "", value: "" }] })
                          }
                        >
                          <PlusCircle className="h-3 w-3" />
                          Add
                        </Button>
                      </div>
                      {cfg.extra.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Add any key-value params supported by the provider (e.g. top_p, top_k, stop_sequences)
                        </p>
                      )}
                      {cfg.extra.map((kv, ki) => (
                        <div key={ki} className="mb-2 grid grid-cols-[1fr_1fr_auto] gap-2">
                          <Input
                            value={kv.key}
                            placeholder="param name"
                            onChange={(e) =>
                              updateCfg({
                                extra: cfg.extra.map((item, idx) =>
                                  idx === ki ? { ...item, key: e.target.value } : item,
                                ),
                              })
                            }
                          />
                          <Input
                            value={kv.value}
                            placeholder="value"
                            onChange={(e) =>
                              updateCfg({
                                extra: cfg.extra.map((item, idx) =>
                                  idx === ki ? { ...item, value: e.target.value } : item,
                                ),
                              })
                            }
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              updateCfg({
                                extra: cfg.extra.filter((_, idx) => idx !== ki),
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </Card>

            {applicationId === "ai-tutor" && (
              <NotepadModelsCard
                configs={formData.notepad_models}
                onChange={(notepad_models) => setFormData((p) => ({ ...p, notepad_models }))}
              />
            )}

            <SttTiersEditor
              tiers={formData.stt_tiers}
              language={formData.stt_language}
              onChange={(tiers) => setFormData((p) => ({ ...p, stt_tiers: tiers }))}
              onLanguageChange={(language) => setFormData((p) => ({ ...p, stt_language: language }))}
            />

            <Card className="space-y-3 p-4">
              <h4 className="font-medium">TTS Config <span className="text-xs font-normal text-muted-foreground">(optional)</span></h4>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Provider</Label>
                  <Select
                    value={formData.tts_provider || TTS_PROVIDER_UNSET}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, tts_provider: v === TTS_PROVIDER_UNSET ? "" : v, tts_model: "" }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {TTS_PROVIDERS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Model</Label>
                  <Select
                    value={formData.tts_model || TTS_MODEL_UNSET}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, tts_model: v === TTS_MODEL_UNSET ? "" : v }))
                    }
                    disabled={!formData.tts_provider}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={formData.tts_provider ? "Select model" : "Select provider first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(TTS_MODELS_BY_PROVIDER[formData.tts_provider] ?? []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            <Card className="space-y-4 p-4">
              <h4 className="font-medium">Canned Messages <span className="text-xs font-normal text-muted-foreground">(optional)</span></h4>

              {/* AI Tutor canned messages */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-muted-foreground">{applicationId === "ai-tutor" ? "AI Tutor" : applicationId}</h5>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const existing = new Set(formData.canned_messages.map((c) => c.prompt_id));
                        const newMessages = cannedMessagePrompts
                          .filter((c) => !existing.has(c.prompt_id))
                          .map((c) => ({
                            prompt_id: c.prompt_id,
                            version: Number.parseInt(c.active_version, 10) || 1,
                          }));
                        setFormData((prev) => ({ ...prev, canned_messages: [...prev.canned_messages, ...newMessages] }));
                      }}
                    >
                      Add All
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          canned_messages: [...p.canned_messages, { prompt_id: "", version: 1 }],
                        }))
                      }
                    >
                      Add Message
                    </Button>
                  </div>
                </div>
                {formData.canned_messages.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No canned messages added.</p>
                ) : (
                  <div className="space-y-2">
                    {formData.canned_messages.map((ref, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Select
                          value={ref.prompt_id || "none"}
                          onValueChange={async (value) => {
                            const nextId = value === "none" ? "" : value;
                            setFormData((p) => ({
                              ...p,
                              canned_messages: p.canned_messages.map((r, i) =>
                                i === idx ? { ...r, prompt_id: nextId } : r
                              ),
                            }));
                            if (nextId) await loadVersionsForPrompt(nextId);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select canned message" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select canned message...</SelectItem>
                            {cannedMessagePrompts.map((c) => (
                              <SelectItem key={c.prompt_id} value={c.prompt_id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={String(ref.version)}
                          onOpenChange={(open) => { if (open && ref.prompt_id) loadVersionsForPrompt(ref.prompt_id); }}
                          onValueChange={(value) =>
                            setFormData((p) => ({
                              ...p,
                              canned_messages: p.canned_messages.map((r, i) =>
                                i === idx ? { ...r, version: Number.parseInt(value, 10) || 1 } : r
                              ),
                            }))
                          }
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Version" />
                          </SelectTrigger>
                          <SelectContent>
                            {versionOptionsFor(ref.prompt_id, ref.version).map((v) => (
                              <SelectItem key={v} value={String(v)}>
                                v{v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              canned_messages: p.canned_messages.filter((_, i) => i !== idx),
                            }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Gateway canned messages */}
              {applicationId === "ai-tutor" && (
                <div className="space-y-3 border-t pt-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-sm font-medium text-muted-foreground">Gateway</h5>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const existing = new Set(formData.gateway_canned_messages.map((c) => c.prompt_id));
                          const newMessages = gatewayCannedMessagePrompts
                            .filter((c) => !existing.has(c.prompt_id))
                            .map((c) => ({
                              prompt_id: c.prompt_id,
                              version: Number.parseInt(c.active_version, 10) || 1,
                            }));
                          setFormData((prev) => ({ ...prev, gateway_canned_messages: [...prev.gateway_canned_messages, ...newMessages] }));
                        }}
                      >
                        Add All
                      </Button>
                      <Button
                        size="sm"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            gateway_canned_messages: [...p.gateway_canned_messages, { prompt_id: "", version: 1 }],
                          }))
                        }
                      >
                        Add Message
                      </Button>
                    </div>
                  </div>
                  {formData.gateway_canned_messages.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No gateway canned messages added.</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.gateway_canned_messages.map((ref, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Select
                            value={ref.prompt_id || "none"}
                            onValueChange={async (value) => {
                              const nextId = value === "none" ? "" : value;
                              setFormData((p) => ({
                                ...p,
                                gateway_canned_messages: p.gateway_canned_messages.map((r, i) =>
                                  i === idx ? { ...r, prompt_id: nextId } : r
                                ),
                              }));
                              if (nextId) await loadVersionsForPrompt(nextId);
                            }}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select canned message" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select canned message...</SelectItem>
                              {gatewayCannedMessagePrompts.map((c) => (
                                <SelectItem key={c.prompt_id} value={c.prompt_id}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={String(ref.version)}
                            onOpenChange={(open) => { if (open && ref.prompt_id) loadVersionsForPrompt(ref.prompt_id, "gateway"); }}
                            onValueChange={(value) =>
                              setFormData((p) => ({
                                ...p,
                                gateway_canned_messages: p.gateway_canned_messages.map((r, i) =>
                                  i === idx ? { ...r, version: Number.parseInt(value, 10) || 1 } : r
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue placeholder="Version" />
                            </SelectTrigger>
                            <SelectContent>
                              {versionOptionsFor(ref.prompt_id, ref.version).map((v) => (
                                <SelectItem key={v} value={String(v)}>
                                  v{v}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setFormData((p) => ({
                                ...p,
                                gateway_canned_messages: p.gateway_canned_messages.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>

            <PromptsCard
              title={applicationId === "ai-tutor" ? "AI Tutor Prompts" : "Prompt Snapshot"}
              formData={formData}
              setFormData={setFormData}
              availablePrompts={prompts}
              promptVersionsMap={promptVersionsMap}
              loadVersionsForPrompt={loadVersionsForPrompt}
              getPromptAppId={getPromptAppId}
              filterAppId={applicationId}
            />

            {applicationId === "ai-tutor" && (
              <PromptsCard
                title="Gateway Prompts"
                formData={formData}
                setFormData={setFormData}
                availablePrompts={offeredGatewayPrompts}
                promptVersionsMap={promptVersionsMap}
                loadVersionsForPrompt={loadVersionsForPrompt}
                getPromptAppId={getPromptAppId}
                filterAppId="gateway"
                groups={GATEWAY_PROMPT_GROUPS}
              />
            )}
          </div>

          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {(() => {
              const missing: string[] = [];
              for (const p of formData.prompts) {
                const meta = allAvailablePrompts.find((pr) => pr.prompt_id === p.prompt_id);
                if (!meta?.placeholders) continue;
                for (const ph of meta.placeholders) {
                  if (!ph.required) continue;
                  if (ph.description === "__runtime__") continue; // filled at runtime, no slot needed
                  const slot = p.slot_assignments?.find((s) => s.placeholder_name === ph.name);
                  if (!slot || slot.options.length === 0) {
                    missing.push(`${meta.name} → {{${ph.name}}}`);
                  }
                }
              }
              return missing.length > 0 ? (
                <p className="text-xs text-destructive mr-auto">
                  Required slots missing options: {missing.join(", ")}
                </p>
              ) : null;
            })()}
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSaving || !promptsReady || !formData.name.trim()}>
              {isSaving ? "Saving..." : createMode === "dev" ? "Create Dev Version" : "Create Tutor Version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedTV)} onOpenChange={(open) => { if (!open) { setSelectedTV(null); setEditDraft(null); setIsEditingDraft(false); setResolvedTV(null); setResolvedPreviewPromptId(null); setSiblingTV(null); setResolvedSiblingTV(null); setResolvedSiblingPreviewPromptId(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {selectedTV && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedTV.status === "draft" && editDraft ? (
                    <Input
                      value={editDraft.name}
                      onChange={(e) => setEditDraft((p) => p ? { ...p, name: e.target.value } : p)}
                      className="max-w-[300px] font-semibold"
                    />
                  ) : (
                    selectedTV.name
                  )}
                  <Badge variant="outline">v{selectedTV.version_number}</Badge>
                  <Badge>{selectedTV.status}</Badge>
                  {selectedTV.is_default && <Badge variant="default">default</Badge>}
                  {selectedTV.org && <Badge variant="secondary">{orgIdToName[selectedTV.org] || selectedTV.org}</Badge>}
                </DialogTitle>
                <p className="font-mono text-xs text-muted-foreground">ID: {selectedTV.tutor_version_id}</p>
                {selectedTV.status === "draft" && editDraft ? (
                  <textarea
                    value={editDraft.description}
                    onChange={(e) => setEditDraft((p) => p ? { ...p, description: e.target.value } : p)}
                    className="min-h-16 rounded-md border bg-background px-3 py-2 text-sm"
                    placeholder="Description..."
                  />
                ) : (
                  <DialogDescription>{selectedTV.description || "No description"}</DialogDescription>
                )}
              </DialogHeader>

              {isEditingDraft ? (
                /* Full edit form for draft tutor versions */
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Tutor version name"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Description</Label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                      className="min-h-20 rounded-md border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Organization</Label>
                    <OrgCombobox
                      value={formData.org || undefined}
                      onChange={(id) => setFormData((p) => ({ ...p, org: id ?? "" }))}
                    />
                  </div>

                  <Card className="space-y-3 p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">LLM Configs</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            llm_configs: [
                              ...p.llm_configs,
                              { ...defaultLLMConfig, name: `config-${p.llm_configs.length + 1}`, model: "" },
                            ],
                          }))
                        }
                      >
                        <PlusCircle className="h-3 w-3" />
                        Add Config
                      </Button>
                    </div>

                    {formData.llm_configs.map((cfg, ci) => {
                      const updateCfg = (patch: Partial<LLMConfigEntry>) =>
                        setFormData((p) => ({
                          ...p,
                          llm_configs: p.llm_configs.map((c, idx) =>
                            idx === ci ? { ...c, ...patch } : c,
                          ),
                        }));
                      return (
                        <div key={ci} className="space-y-3 rounded-md border p-3">
                          <div className="flex items-center gap-2">
                            <Input
                              value={cfg.name}
                              onChange={(e) => updateCfg({ name: e.target.value })}
                              className="max-w-[200px] font-medium"
                              placeholder="Config name"
                            />
                            {ci === 0 && (
                              <Badge variant="secondary" className="text-xs">primary</Badge>
                            )}
                            {ci > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="ml-auto"
                                onClick={() =>
                                  setFormData((p) => ({
                                    ...p,
                                    llm_configs: p.llm_configs.filter((_, idx) => idx !== ci),
                                  }))
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="grid gap-2 md:col-span-2">
                              <Label>Model</Label>
                              <Input
                                value={cfg.model}
                                onChange={(e) => updateCfg({ model: e.target.value })}
                                placeholder="e.g. bedrock/claude-sonnet-4-20250514, azure/gpt-5"
                              />
                              <p className="text-xs text-muted-foreground">
                                Use litellm format: provider/model (e.g. bedrock/claude-sonnet-4-20250514, azure/gpt-5)
                              </p>
                            </div>
                            <LlmFallbackTiers
                              primaryModel={cfg.model}
                              fallbacks={cfg.fallbacks}
                              onChange={(fallbacks) => updateCfg({ fallbacks })}
                            />
                            <div className="grid gap-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`edit-temp-toggle-${ci}`}
                                  checked={cfg.temperature != null}
                                  onChange={(e) =>
                                    updateCfg({ temperature: e.target.checked ? 0.7 : null })
                                  }
                                  className="h-3.5 w-3.5"
                                />
                                <Label htmlFor={`edit-temp-toggle-${ci}`}>Temperature</Label>
                              </div>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                max="2"
                                disabled={cfg.temperature == null}
                                value={cfg.temperature ?? ""}
                                onChange={(e) =>
                                  updateCfg({
                                    temperature: e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                                placeholder="Not set"
                              />
                            </div>
                            <div className="grid gap-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id={`edit-tokens-toggle-${ci}`}
                                  checked={cfg.max_tokens != null}
                                  onChange={(e) =>
                                    updateCfg({ max_tokens: e.target.checked ? 4096 : null })
                                  }
                                  className="h-3.5 w-3.5"
                                />
                                <Label htmlFor={`edit-tokens-toggle-${ci}`}>Max tokens</Label>
                              </div>
                              <Input
                                type="number"
                                disabled={cfg.max_tokens == null}
                                value={cfg.max_tokens ?? ""}
                                onChange={(e) =>
                                  updateCfg({
                                    max_tokens: e.target.value === "" ? null : Number(e.target.value),
                                  })
                                }
                                placeholder="Not set"
                              />
                            </div>
                          </div>
                          {/* Additional params */}
                          <div className="border-t pt-2">
                            <div className="mb-2 flex items-center justify-between">
                              <Label className="text-xs text-muted-foreground">Additional Parameters</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  updateCfg({ extra: [...cfg.extra, { key: "", value: "" }] })
                                }
                              >
                                <PlusCircle className="h-3 w-3" />
                                Add
                              </Button>
                            </div>
                            {cfg.extra.length === 0 && (
                              <p className="text-xs text-muted-foreground">
                                Add any key-value params supported by the provider (e.g. top_p, top_k, stop_sequences)
                              </p>
                            )}
                            {cfg.extra.map((kv, ki) => (
                              <div key={ki} className="mb-2 grid grid-cols-[1fr_1fr_auto] gap-2">
                                <Input
                                  value={kv.key}
                                  placeholder="param name"
                                  onChange={(e) =>
                                    updateCfg({
                                      extra: cfg.extra.map((item, idx) =>
                                        idx === ki ? { ...item, key: e.target.value } : item,
                                      ),
                                    })
                                  }
                                />
                                <Input
                                  value={kv.value}
                                  placeholder="value"
                                  onChange={(e) =>
                                    updateCfg({
                                      extra: cfg.extra.map((item, idx) =>
                                        idx === ki ? { ...item, value: e.target.value } : item,
                                      ),
                                    })
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    updateCfg({
                                      extra: cfg.extra.filter((_, idx) => idx !== ki),
                                    })
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </Card>

                  {applicationId === "ai-tutor" && (
                    <NotepadModelsCard
                      configs={formData.notepad_models}
                      missingGatewayRecord={!siblingTV}
                      onChange={(notepad_models) => setFormData((p) => ({ ...p, notepad_models }))}
                    />
                  )}

                  <SttTiersEditor
                    tiers={formData.stt_tiers}
                    language={formData.stt_language}
                    onChange={(tiers) => setFormData((p) => ({ ...p, stt_tiers: tiers }))}
                    onLanguageChange={(language) => setFormData((p) => ({ ...p, stt_language: language }))}
                  />

                  <Card className="space-y-3 p-4">
                    <h4 className="font-medium">TTS Config <span className="text-xs font-normal text-muted-foreground">(optional)</span></h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Provider</Label>
                        <Select
                          value={formData.tts_provider || TTS_PROVIDER_UNSET}
                          onValueChange={(v) =>
                            setFormData((p) => ({ ...p, tts_provider: v === TTS_PROVIDER_UNSET ? "" : v, tts_model: "" }))
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select provider" />
                          </SelectTrigger>
                          <SelectContent>
                            {TTS_PROVIDERS.map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Model</Label>
                        <Select
                          value={formData.tts_model || TTS_MODEL_UNSET}
                          onValueChange={(v) =>
                            setFormData((p) => ({ ...p, tts_model: v === TTS_MODEL_UNSET ? "" : v }))
                          }
                          disabled={!formData.tts_provider}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={formData.tts_provider ? "Select model" : "Select provider first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {(TTS_MODELS_BY_PROVIDER[formData.tts_provider] ?? []).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>


                  <Card className="space-y-4 p-4">
                    <h4 className="font-medium">Canned Messages <span className="text-xs font-normal text-muted-foreground">(optional)</span></h4>

                    {/* AI Tutor canned messages */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="text-sm font-medium text-muted-foreground">{applicationId === "ai-tutor" ? "AI Tutor" : applicationId}</h5>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const existing = new Set(formData.canned_messages.map((c) => c.prompt_id));
                              const newMessages = cannedMessagePrompts
                                .filter((c) => !existing.has(c.prompt_id))
                                .map((c) => ({
                                  prompt_id: c.prompt_id,
                                  version: Number.parseInt(c.active_version, 10) || 1,
                                }));
                              setFormData((prev) => ({ ...prev, canned_messages: [...prev.canned_messages, ...newMessages] }));
                            }}
                          >
                            Add All
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              setFormData((p) => ({
                                ...p,
                                canned_messages: [...p.canned_messages, { prompt_id: "", version: 1 }],
                              }))
                            }
                          >
                            Add Message
                          </Button>
                        </div>
                      </div>
                      {formData.canned_messages.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No canned messages added.</p>
                      ) : (
                        <div className="space-y-2">
                          {formData.canned_messages.map((ref, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Select
                                value={ref.prompt_id || "none"}
                                onValueChange={async (value) => {
                                  const nextId = value === "none" ? "" : value;
                                  setFormData((p) => ({
                                    ...p,
                                    canned_messages: p.canned_messages.map((r, i) =>
                                      i === idx ? { ...r, prompt_id: nextId } : r
                                    ),
                                  }));
                                  if (nextId) await loadVersionsForPrompt(nextId);
                                }}
                              >
                                <SelectTrigger className="flex-1">
                                  <SelectValue placeholder="Select canned message" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select canned message...</SelectItem>
                                  {cannedMessagePrompts.map((c) => (
                                    <SelectItem key={c.prompt_id} value={c.prompt_id}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={String(ref.version)}
                                onOpenChange={(open) => { if (open && ref.prompt_id) loadVersionsForPrompt(ref.prompt_id); }}
                                onValueChange={(value) =>
                                  setFormData((p) => ({
                                    ...p,
                                    canned_messages: p.canned_messages.map((r, i) =>
                                      i === idx ? { ...r, version: Number.parseInt(value, 10) || 1 } : r
                                    ),
                                  }))
                                }
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="Version" />
                                </SelectTrigger>
                                <SelectContent>
                                  {versionOptionsFor(ref.prompt_id, ref.version).map((v) => (
                                    <SelectItem key={v} value={String(v)}>
                                      v{v}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setFormData((p) => ({
                                    ...p,
                                    canned_messages: p.canned_messages.filter((_, i) => i !== idx),
                                  }))
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Gateway canned messages */}
                    {applicationId === "ai-tutor" && (
                      <div className="space-y-3 border-t pt-3">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-medium text-muted-foreground">Gateway</h5>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const existing = new Set(formData.gateway_canned_messages.map((c) => c.prompt_id));
                                const newMessages = gatewayCannedMessagePrompts
                                  .filter((c) => !existing.has(c.prompt_id))
                                  .map((c) => ({
                                    prompt_id: c.prompt_id,
                                    version: Number.parseInt(c.active_version, 10) || 1,
                                  }));
                                setFormData((prev) => ({ ...prev, gateway_canned_messages: [...prev.gateway_canned_messages, ...newMessages] }));
                              }}
                            >
                              Add All
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                setFormData((p) => ({
                                  ...p,
                                  gateway_canned_messages: [...p.gateway_canned_messages, { prompt_id: "", version: 1 }],
                                }))
                              }
                            >
                              Add Message
                            </Button>
                          </div>
                        </div>
                        {formData.gateway_canned_messages.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No gateway canned messages added.</p>
                        ) : (
                          <div className="space-y-2">
                            {formData.gateway_canned_messages.map((ref, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Select
                                  value={ref.prompt_id || "none"}
                                  onValueChange={async (value) => {
                                    const nextId = value === "none" ? "" : value;
                                    setFormData((p) => ({
                                      ...p,
                                      gateway_canned_messages: p.gateway_canned_messages.map((r, i) =>
                                        i === idx ? { ...r, prompt_id: nextId } : r
                                      ),
                                    }));
                                    if (nextId) await loadVersionsForPrompt(nextId);
                                  }}
                                >
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Select canned message" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Select canned message...</SelectItem>
                                    {gatewayCannedMessagePrompts.map((c) => (
                                      <SelectItem key={c.prompt_id} value={c.prompt_id}>
                                        {c.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={String(ref.version)}
                                  onOpenChange={(open) => { if (open && ref.prompt_id) loadVersionsForPrompt(ref.prompt_id, "gateway"); }}
                                  onValueChange={(value) =>
                                    setFormData((p) => ({
                                      ...p,
                                      gateway_canned_messages: p.gateway_canned_messages.map((r, i) =>
                                        i === idx ? { ...r, version: Number.parseInt(value, 10) || 1 } : r
                                      ),
                                    }))
                                  }
                                >
                                  <SelectTrigger className="w-24">
                                    <SelectValue placeholder="Version" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {versionOptionsFor(ref.prompt_id, ref.version).map((v) => (
                                      <SelectItem key={v} value={String(v)}>
                                        v{v}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    setFormData((p) => ({
                                      ...p,
                                      gateway_canned_messages: p.gateway_canned_messages.filter((_, i) => i !== idx),
                                    }))
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>

                  <PromptsCard
                    title={applicationId === "ai-tutor" ? "AI Tutor Prompts" : "Prompt Snapshot"}
                    formData={formData}
                    setFormData={setFormData}
                    availablePrompts={prompts}
                    promptVersionsMap={promptVersionsMap}
                    loadVersionsForPrompt={loadVersionsForPrompt}
                    getPromptAppId={getPromptAppId}
                    filterAppId={applicationId}
                  />
                  {applicationId === "ai-tutor" && (
                    <PromptsCard
                      title="Gateway Prompts"
                      formData={formData}
                      setFormData={setFormData}
                      availablePrompts={offeredGatewayPrompts}
                      promptVersionsMap={promptVersionsMap}
                      loadVersionsForPrompt={loadVersionsForPrompt}
                      getPromptAppId={getPromptAppId}
                      filterAppId="gateway"
                      groups={GATEWAY_PROMPT_GROUPS}
                    />
                  )}
                </div>
              ) : (
                /* Read-only view for non-editing mode */
                <div className="space-y-3 text-sm">
                  <Card className="space-y-2 p-3">
                    <h4 className="font-medium">Runtime</h4>
                    <p>
                      Org: <span className="font-medium">{selectedTV.org ? (orgIdToName[selectedTV.org] || selectedTV.org) : "core"}</span>
                    </p>
                    <p className="break-all rounded bg-muted px-2 py-1 font-mono text-xs">
                      /api/v1/runtime/tutor-version/{selectedTV.application_id}/{selectedTV.tutor_version_id}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => copyRuntimeEndpoint(selectedTV)}>
                      {copiedEndpoint ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copiedEndpoint ? "Copied" : "Copy endpoint"}
                    </Button>
                  </Card>

                  <Card className="space-y-2 p-3">
                    <h4 className="font-medium">LLM Config{selectedConfigs.generic.length ? "s" : ""}</h4>
                    {(selectedConfigs.generic.length
                      ? selectedConfigs.generic
                      : [{ name: "default", config: selectedTV.llm_config }]
                    ).map((nc) => (
                      <div key={nc.name} className="rounded border p-2">
                        {selectedConfigs.generic.length > 1 && (
                          <p className="mb-1 font-medium text-xs">{nc.name}</p>
                        )}
                        <p className="break-all">Model: {nc.config.model}</p>
                        {!!nc.config.fallbacks?.length && (
                          <p className="break-all">
                            <span className="font-medium">Fallback chain</span>
                            <span className="text-muted-foreground"> · </span>
                            {[nc.config.model, ...nc.config.fallbacks.map((f) => f.model)].join(" → ")}
                          </p>
                        )}
                        {nc.config.temperature != null && (
                          <p>Temperature: {nc.config.temperature}</p>
                        )}
                        {nc.config.max_tokens != null && (
                          <p>Max Tokens: {(nc.config.max_tokens as number).toLocaleString()}</p>
                        )}
                        {Object.entries(nc.config)
                          .filter(([k]) => !["provider", "model", "temperature", "max_tokens", "fallbacks"].includes(k))
                          .map(([k, v]) => (
                            <p key={k}>
                              {k}: <span className="font-mono">{String(v)}</span>
                            </p>
                          ))
                        }
                      </div>
                    ))}
                    <p className="text-muted-foreground">Created: {formatDate(selectedTV.created_at)}</p>
                  </Card>

                  {applicationId === "ai-tutor" && (
                    <NotepadModelsCard configs={selectedConfigs.notepad} missingGatewayRecord={!siblingLoading && !siblingTV} />
                  )}

                  {selectedTV.stt_config && (() => {
                    const sttTiers = sttTiersFromConfig(selectedTV.stt_config);
                    return (
                      <Card className="space-y-2 p-3">
                        <h4 className="font-medium">STT Config</h4>
                        <div className="space-y-1">
                          {sttTiers.map((tier, idx) => {
                            const isPrimary = idx === 0;
                            return (
                              <p key={`${tier.provider}-${idx}`}>
                                <span className="font-medium">{isPrimary ? "Primary" : `Fallback ${idx}`}</span>
                                <span className="text-muted-foreground"> · </span>
                                {tier.provider} / {tier.model}
                              </p>
                            );
                          })}
                        </div>
                        {selectedTV.stt_config.language && (
                          <p className="text-muted-foreground">Language: {selectedTV.stt_config.language}</p>
                        )}
                      </Card>
                    );
                  })()}

                  {selectedTV.tts_config && (
                    <Card className="space-y-2 p-3">
                      <h4 className="font-medium">TTS Config</h4>
                      <p>Provider: {selectedTV.tts_config.provider}</p>
                      <p>Model: {TTS_MODEL_OPTIONS.find((o) => o.value === selectedTV.tts_config!.model)?.label ?? selectedTV.tts_config.model}</p>
                    </Card>
                  )}

                  <Card className="space-y-2 p-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{applicationId === "ai-tutor" ? "AI Tutor Prompts" : "Prompt Snapshots"}</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isResolvingTV}
                        onClick={async () => {
                          if (resolvedTV) {
                            setResolvedTV(null);
                            setResolvedPreviewPromptId(null);
                            return;
                          }
                          setIsResolvingTV(true);
                          try {
                            const resolved = await apiService.resolveTutorVersion(selectedTV.application_id, selectedTV.tutor_version_id);
                            setResolvedTV(resolved);
                          } catch {
                            setError("Failed to resolve tutor version");
                          } finally {
                            setIsResolvingTV(false);
                          }
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        {isResolvingTV ? "Resolving..." : resolvedTV ? "Hide resolved" : "Preview resolved"}
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {selectedTV.prompts.map((ps, index) => {
                        const resolvedContent = resolvedTV?.prompts.find((rp) => rp.prompt_id === ps.prompt_id)?.content;
                        const isResolvingThis = resolvedPreviewPromptId === ps.prompt_id;
                        return (
                          <div key={`${ps.prompt_id}-${index}`}>
                            <div className="flex items-center justify-between gap-2 rounded border px-2 py-1">
                              <span className="truncate">{ps.prompt_id}</span>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline">v{ps.version}</Badge>
                                {ps.model && <Badge variant="secondary">{ps.model}</Badge>}
                                {ps.llm_config_name && <Badge variant="secondary">{ps.llm_config_name}</Badge>}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  title="Preview raw prompt content"
                                  onClick={async () => {
                                    if (previewPrompt?.prompt_id === ps.prompt_id && previewPrompt?.version === ps.version) {
                                      setPreviewPrompt(null);
                                      return;
                                    }
                                    try {
                                      const ver = await apiService.getPromptVersion(selectedTV.application_id, ps.prompt_id, String(ps.version));
                                      setPreviewPrompt({ prompt_id: ps.prompt_id, version: ps.version, content: ver.content });
                                    } catch {
                                      setPreviewPrompt({ prompt_id: ps.prompt_id, version: ps.version, content: "(Failed to load)" });
                                    }
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                                {resolvedContent !== undefined && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs"
                                    title="Show resolved content in modal"
                                    onClick={() => setResolvedPreviewPromptId(isResolvingThis ? null : ps.prompt_id)}
                                  >
                                    {isResolvingThis ? "Hide" : "Full preview"}
                                  </Button>
                                )}
                              </div>
                            </div>
                            {previewPrompt?.prompt_id === ps.prompt_id && previewPrompt?.version === ps.version && (
                              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs">
                                {previewPrompt.content}
                              </pre>
                            )}
                            {ps.slot_assignments && ps.slot_assignments.length > 0 && (() => {
                              const topSlots = ps.slot_assignments.filter((s) => !s.placeholder_name.includes("."));
                              return (
                              <div className="ml-3 mt-1 space-y-1 border-l-2 border-muted pl-2">
                                {topSlots.map((slot) => {
                                  const childPrefix = `${slot.placeholder_name}.`;
                                  const childSlots = ps.slot_assignments!.filter((s) => s.placeholder_name.startsWith(childPrefix));
                                  return (
                                  <div key={slot.placeholder_name}>
                                    <div className="text-xs">
                                      <span className="font-mono text-muted-foreground">{`{${slot.placeholder_name}}`}</span>
                                      <span className="text-muted-foreground"> → </span>
                                      <span className="space-x-1">
                                        {slot.options.map((opt) => (
                                          <span key={String(opt.runtime_variable ?? `${opt.prompt_id}-${opt.version}`)} className="inline-flex items-center gap-1">
                                            {opt.runtime_variable !== undefined ? (
                                              <Badge variant="secondary" className="text-[10px] px-1 py-0">runtime</Badge>
                                            ) : (
                                              <>
                                                <span>{opt.prompt_id}</span>
                                                <Badge variant="outline" className="text-[10px] px-1 py-0">v{opt.version}</Badge>
                                              </>
                                            )}
                                            {opt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                                          </span>
                                        ))}
                                      </span>
                                    </div>
                                    {childSlots.length > 0 && (
                                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-muted-foreground/20 pl-2">
                                        {childSlots.map((cs) => (
                                          <div key={cs.placeholder_name} className="text-xs">
                                            <span className="font-mono text-muted-foreground">{`{${cs.placeholder_name.slice(childPrefix.length)}}`}</span>
                                            <span className="text-muted-foreground"> → </span>
                                            <span className="space-x-1">
                                              {cs.options.map((opt) => (
                                                <span key={String(opt.runtime_variable ?? `${opt.prompt_id}-${opt.version}`)} className="inline-flex items-center gap-1">
                                                  {opt.runtime_variable !== undefined ? (
                                                    <Badge variant="secondary" className="text-[10px] px-1 py-0">runtime</Badge>
                                                  ) : (
                                                    <>
                                                      <span>{opt.prompt_id}</span>
                                                      <Badge variant="outline" className="text-[10px] px-1 py-0">v{opt.version}</Badge>
                                                    </>
                                                  )}
                                                  {opt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                                                </span>
                                              ))}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </Card>

                  {siblingTV && applicationId === "ai-tutor" && (() => {
                    // Build a map from prompt_id → PromptSnapshot for second-level slot lookup
                    const siblingPromptMap = new Map(siblingTV.prompts.map((p) => [p.prompt_id, p]));
                    // Collect all prompt_ids referenced as slot options so we can hide them from top-level
                    const gwSlotOptionIds = new Set<string>();
                    for (const p of siblingTV.prompts) {
                      for (const slot of p.slot_assignments ?? []) {
                        for (const opt of slot.options) {
                          if (opt.prompt_id) gwSlotOptionIds.add(opt.prompt_id);
                        }
                      }
                    }
                    const gwTopLevelPrompts = siblingTV.prompts.filter((p) => !gwSlotOptionIds.has(p.prompt_id));
                    return (
                    <Card className="space-y-2 p-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Gateway Prompts</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isResolvingSiblingTV}
                          onClick={async () => {
                            if (resolvedSiblingTV) {
                              setResolvedSiblingTV(null);
                              setResolvedSiblingPreviewPromptId(null);
                              return;
                            }
                            setIsResolvingSiblingTV(true);
                            try {
                              const resolved = await apiService.resolveTutorVersion("gateway", siblingTV.tutor_version_id);
                              setResolvedSiblingTV(resolved);
                            } catch {
                              setError("Failed to resolve gateway tutor version");
                            } finally {
                              setIsResolvingSiblingTV(false);
                            }
                          }}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {isResolvingSiblingTV ? "Resolving..." : resolvedSiblingTV ? "Hide resolved" : "Preview resolved"}
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {gwTopLevelPrompts.map((ps, index) => {
                          const resolvedContent = resolvedSiblingTV?.prompts.find((rp) => rp.prompt_id === ps.prompt_id)?.content;
                          const isResolvingThis = resolvedSiblingPreviewPromptId === ps.prompt_id;
                          return (
                          <div key={`gw-${ps.prompt_id}-${index}`}>
                            <div className="flex items-center justify-between gap-2 rounded border px-2 py-1">
                              <span className="truncate">{ps.prompt_id}</span>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline">v{ps.version}</Badge>
                                {ps.model && <Badge variant="secondary">{ps.model}</Badge>}
                                {ps.llm_config_name && <Badge variant="secondary">{ps.llm_config_name}</Badge>}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  title="Preview raw prompt content"
                                  onClick={async () => {
                                    if (previewPrompt?.prompt_id === ps.prompt_id && previewPrompt?.version === ps.version) {
                                      setPreviewPrompt(null);
                                      return;
                                    }
                                    try {
                                      const ver = await apiService.getPromptVersion("gateway", ps.prompt_id, String(ps.version));
                                      setPreviewPrompt({ prompt_id: ps.prompt_id, version: ps.version, content: ver.content });
                                    } catch {
                                      setPreviewPrompt({ prompt_id: ps.prompt_id, version: ps.version, content: "(Failed to load)" });
                                    }
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                                {resolvedContent !== undefined && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-xs"
                                    title="Show resolved content in modal"
                                    onClick={() => setResolvedSiblingPreviewPromptId(isResolvingThis ? null : ps.prompt_id)}
                                  >
                                    {isResolvingThis ? "Hide" : "Full preview"}
                                  </Button>
                                )}
                              </div>
                            </div>
                            {previewPrompt?.prompt_id === ps.prompt_id && previewPrompt?.version === ps.version && (
                              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs">
                                {previewPrompt.content}
                              </pre>
                            )}
                            {ps.slot_assignments && ps.slot_assignments.length > 0 && (
                              <div className="ml-3 mt-1 space-y-1 border-l-2 border-muted pl-2">
                                {ps.slot_assignments.map((slot) => {
                                  return (
                                  <div key={slot.placeholder_name}>
                                    <div className="text-xs">
                                      <span className="font-mono text-muted-foreground">{`{${slot.placeholder_name}}`}</span>
                                      <span className="text-muted-foreground"> → </span>
                                      <span className="space-x-1">
                                        {slot.options.map((opt) => (
                                          <span key={String(opt.runtime_variable ?? `${opt.prompt_id}-${opt.version}`)} className="inline-flex items-center gap-1">
                                            {opt.runtime_variable !== undefined ? (
                                              <Badge variant="secondary" className="text-[10px] px-1 py-0">runtime</Badge>
                                            ) : (
                                              <>
                                                <span>{opt.prompt_id}</span>
                                                <Badge variant="outline" className="text-[10px] px-1 py-0">v{opt.version}</Badge>
                                              </>
                                            )}
                                            {opt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                                          </span>
                                        ))}
                                      </span>
                                    </div>
                                    {/* Second-level: look up the referenced prompt's own slot_assignments */}
                                    {slot.options.filter((o) => o.prompt_id && siblingPromptMap.has(o.prompt_id)).map((opt) => {
                                      const refPrompt = siblingPromptMap.get(opt.prompt_id!);
                                      if (!refPrompt?.slot_assignments?.length) return null;
                                      return (
                                        <div key={`${slot.placeholder_name}-${opt.prompt_id}-slots`} className="ml-4 mt-1 space-y-1 border-l-2 border-muted-foreground/20 pl-2">
                                          {refPrompt.slot_assignments.map((subSlot) => (
                                            <div key={subSlot.placeholder_name} className="text-xs">
                                              <span className="font-mono text-muted-foreground">{`{${subSlot.placeholder_name}}`}</span>
                                              <span className="text-muted-foreground"> → </span>
                                              <span className="space-x-1">
                                                {subSlot.options.map((subOpt) => (
                                                  <span key={String(subOpt.runtime_variable ?? `${subOpt.prompt_id}-${subOpt.version}`)} className="inline-flex items-center gap-1">
                                                    {subOpt.runtime_variable !== undefined ? (
                                                      <Badge variant="secondary" className="text-[10px] px-1 py-0">runtime</Badge>
                                                    ) : (
                                                      <>
                                                        <span>{subOpt.prompt_id}</span>
                                                        <Badge variant="outline" className="text-[10px] px-1 py-0">v{subOpt.version}</Badge>
                                                      </>
                                                    )}
                                                    {subOpt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                                                  </span>
                                                ))}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </Card>
                    );
                  })()}
                </div>
              )}

              <DialogFooter>
                {selectedTV.status === "draft" && (
                  <>
                    <Button variant="destructive" onClick={() => handleDelete(selectedTV)} disabled={isSaving}>
                      Delete
                    </Button>
                    {isEditingDraft ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setIsEditingDraft(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveDraft}
                          disabled={isSaving || !promptsReady || !formData.name.trim()}
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="secondary"
                          onClick={() => startEditingDraft(selectedTV)}
                        >
                          Edit
                        </Button>
                        <Button onClick={() => handlePublish(selectedTV)} disabled={isSaving}>
                          {isSaving ? "Publishing..." : "Publish"}
                        </Button>
                      </>
                    )}
                  </>
                )}
                {selectedTV.status === "published" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleExport(selectedTV)}
                      disabled={isSaving}
                      title="Download a file to import in another environment's dashboard"
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </Button>
                    {!selectedTV.is_default && (
                      <Button variant="outline" onClick={() => handleSetDefault(selectedTV)} disabled={isSaving}>
                        {isSaving ? "Setting..." : "Set as Default"}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => handleSetOrgDefault(selectedTV)} disabled={isSaving}>
                      Set as Org Default
                    </Button>
                    {Object.values(orgDefaultMap).includes(selectedTV.tutor_version_id) && (
                      <Button variant="outline" onClick={() => handleRemoveOrgDefault(selectedTV)} disabled={isSaving}>
                        Remove as Org Default
                      </Button>
                    )}
                    <Button onClick={() => handleArchive(selectedTV)} disabled={isSaving}>
                      {isSaving ? "Archiving..." : "Archive"}
                    </Button>
                  </>
                )}
                {selectedTV.status === "dev" && (
                  <>
                    <Button variant="destructive" onClick={() => handleDelete(selectedTV)} disabled={isSaving}>
                      Delete
                    </Button>
                    {isEditingDraft ? (
                      <>
                        <Button variant="outline" onClick={() => setIsEditingDraft(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveDraft} disabled={isSaving || !promptsReady || !formData.name.trim()}>
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" onClick={() => startEditingDraft(selectedTV)}>
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!confirm(`Promote dev version "${selectedTV.name}" to a draft?`)) return;
                            setIsSaving(true);
                            try {
                              await apiService.promoteDevTutorVersion(applicationId, selectedTV.tutor_version_id);
                              setError(null);
                              setSelectedTV(null);
                              await loadTutorVersions();
                            } catch (err: any) {
                              setError(err?.message || "Failed to promote dev version");
                            } finally {
                              setIsSaving(false);
                            }
                          }}
                          disabled={isSaving}
                        >
                          {isSaving ? "Promoting..." : "Promote to Draft"}
                        </Button>
                      </>
                    )}
                  </>
                )}
                <Button variant="outline" onClick={() => { setSelectedTV(null); setEditDraft(null); setResolvedTV(null); setResolvedPreviewPromptId(null); setSiblingTV(null); setResolvedSiblingTV(null); setResolvedSiblingPreviewPromptId(null); }}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Unified full resolved prompt preview — uses a proper Dialog so Radix handles focus/scroll/overlay */}
      <Dialog
        open={Boolean((resolvedPreviewPromptId && resolvedTV) || (resolvedSiblingPreviewPromptId && resolvedSiblingTV))}
        onOpenChange={(open) => {
          if (!open) {
            setResolvedPreviewPromptId(null);
            setResolvedSiblingPreviewPromptId(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          {(() => {
            const isGateway = resolvedSiblingPreviewPromptId && resolvedSiblingTV;
            const allResolved = isGateway ? resolvedSiblingTV!.prompts : (resolvedTV?.prompts ?? []);
            const rp = isGateway
              ? resolvedSiblingTV!.prompts.find((p) => p.prompt_id === resolvedSiblingPreviewPromptId)
              : resolvedTV?.prompts.find((p) => p.prompt_id === resolvedPreviewPromptId);
            if (!rp) return null;

            const resolvedPromptsMap = new Map(allResolved.map((p) => [p.prompt_id, p]));

            const renderSlotOption = (opt: (typeof allResolved)[0]["slot_assignments"] extends (infer U)[] | undefined ? U extends { options: (infer O)[] } ? O : never : never) => (
              <div key={String(opt.runtime_variable ?? `${opt.prompt_id}-${opt.version}`)} className="ml-3 mb-1 rounded border p-2">
                {opt.runtime_variable !== undefined ? (
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">runtime</Badge>
                    {opt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span className="font-medium">{opt.prompt_id}</span>
                      {opt.version != null && <Badge variant="outline" className="text-[10px] px-1 py-0">v{opt.version}</Badge>}
                      {opt.default && <Badge variant="default" className="text-[10px] px-1 py-0">DEFAULT</Badge>}
                    </div>
                    {opt.content && <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">{opt.content}</pre>}
                  </>
                )}
              </div>
            );

            const renderSlots = (slots: NonNullable<typeof rp.slot_assignments>, depth: number, visited: Set<string>): JSX.Element => (
              <div className={depth > 0 ? "ml-6 mt-1 space-y-2 border-l-2 border-muted-foreground/20 pl-3" : ""}>
                {slots.map((slot) => (
                  <div key={slot.placeholder_name} className="mb-2">
                    <p className="font-mono text-xs text-muted-foreground mb-1">{`{${slot.placeholder_name}}`}</p>
                    {slot.options.map((opt) => {
                      const subPrompt = opt.prompt_id ? resolvedPromptsMap.get(opt.prompt_id) : undefined;
                      const hasSubSlots = subPrompt?.slot_assignments?.length && !visited.has(opt.prompt_id!);
                      return (
                        <div key={String(opt.runtime_variable ?? `${opt.prompt_id}-${opt.version}`)}>
                          {renderSlotOption(opt)}
                          {hasSubSlots && (() => {
                            const nextVisited = new Set(visited);
                            nextVisited.add(opt.prompt_id!);
                            return (
                              <div className="ml-6 mt-1 mb-2">
                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                  {opt.prompt_id} slots
                                </span>
                                {renderSlots(subPrompt!.slot_assignments!, depth + 1, nextVisited)}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );

            return (
              <>
                <DialogHeader>
                  <DialogTitle>{isGateway ? "Full Resolved Gateway Prompt" : "Full Resolved Prompt"}</DialogTitle>
                  <DialogDescription>{rp.prompt_id} — v{rp.version}</DialogDescription>
                </DialogHeader>
                <pre className="whitespace-pre-wrap break-words font-mono text-xs">{rp.content}</pre>
                {rp.slot_assignments && rp.slot_assignments.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="mb-2 text-xs font-medium text-muted-foreground">Resolved Slot Assignments</h4>
                    {renderSlots(rp.slot_assignments, 0, new Set([rp.prompt_id]))}
                  </div>
                )}
                <DialogFooter>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setResolvedPreviewPromptId(null);
                      setResolvedSiblingPreviewPromptId(null);
                    }}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Remove as Org Default Dialog */}
      <Dialog open={isRemoveOrgDefaultDialogOpen} onOpenChange={(open) => { if (!open) { setIsRemoveOrgDefaultDialogOpen(false); setSelectedRemoveOrgs([]); setTvForRemoveOrgDefault(null); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove as Org Default</DialogTitle>
            <DialogDescription>
              Select the organizations to remove <strong>{tvForRemoveOrgDefault?.name}</strong> as their default tutor version. They will fall back to the global default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {removeOrgDefaultOrgs.length === 0 ? (
              <p className="text-sm text-muted-foreground">This tutor version is not set as default for any organizations.</p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {removeOrgDefaultOrgs.map((org) => {
                  const checked = selectedRemoveOrgs.includes(org.id);
                  return (
                    <label key={org.id} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${checked ? "border-destructive bg-destructive/5" : "border-border/60 hover:bg-muted/50"}`}>
                      <input
                        type="checkbox"
                        value={org.id}
                        checked={checked}
                        onChange={() =>
                          setSelectedRemoveOrgs((prev) =>
                            checked ? prev.filter((id) => id !== org.id) : [...prev, org.id],
                          )
                        }
                        className="h-4 w-4 rounded border-border accent-destructive"
                      />
                      {org.name}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsRemoveOrgDefaultDialogOpen(false); setSelectedRemoveOrgs([]); setTvForRemoveOrgDefault(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemoveOrgDefault} disabled={selectedRemoveOrgs.length === 0 || isSaving}>
              {isSaving ? "Removing..." : `Remove${selectedRemoveOrgs.length > 1 ? ` (${selectedRemoveOrgs.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set as Org Default Dialog */}
      <Dialog open={isOrgDefaultDialogOpen} onOpenChange={(open) => { if (!open) { setIsOrgDefaultDialogOpen(false); setSelectedOrgs([]); setTvForOrgDefault(null); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Set as Org Default</DialogTitle>
            <DialogDescription>
              Select one or more organizations to set <strong>{tvForOrgDefault?.name}</strong> as their default tutor version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {orgDefaultLoadingOrgs ? (
              <p className="text-sm text-muted-foreground">Loading organizations...</p>
            ) : orgDefaultOrgs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No organizations found.</p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {orgDefaultOrgs.map((org) => {
                  const checked = selectedOrgs.includes(org.id);
                  return (
                    <label key={org.id} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${checked ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/50"}`}>
                      <input
                        type="checkbox"
                        value={org.id}
                        checked={checked}
                        onChange={() =>
                          setSelectedOrgs((prev) =>
                            checked ? prev.filter((id) => id !== org.id) : [...prev, org.id],
                          )
                        }
                        className="h-4 w-4 rounded border-border accent-primary"
                      />
                      {org.name}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsOrgDefaultDialogOpen(false); setSelectedOrgs([]); setTvForOrgDefault(null); }}>
              Cancel
            </Button>
            <Button onClick={handleConfirmOrgDefault} disabled={selectedOrgs.length === 0 || isSaving}>
              {isSaving ? "Setting..." : `Set as Org Default${selectedOrgs.length > 1 ? ` (${selectedOrgs.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
